import React, { useState, useEffect, useRef, useMemo, useCallback, memo, Component } from 'react';
import { sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import { 
    Box, Grid, GridItem, Heading, Select as ChakraSelect, Skeleton, Alert, AlertIcon, 
    Text, Flex, Badge, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, StatArrow,
    Table, Thead, Tbody, Tfoot, Tr, Th, Td, TableContainer, Progress, Button, Icon,
    Tabs, TabList, TabPanels, Tab, TabPanel, Tooltip, HStack, Input, FormLabel, FormControl,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    useDisclosure, Link, useToast,
    Menu, MenuButton, MenuList, MenuItem, MenuDivider, Portal, InputGroup, InputLeftElement, Checkbox,
    IconButton
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FiChevronDown, FiSearch, FiFilter, FiLayers, FiUsers, FiTag } from 'react-icons/fi'; 
import { httpsCallable } from 'firebase/functions';
import { functions, auth, db } from '../../utils/init-firebase';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { FiPhone, FiMail, FiCalendar, FiCheckSquare, FiAlertTriangle, FiActivity, FiClock, FiInfo, FiExternalLink, FiRefreshCw, FiCopy, FiMessageSquare } from 'react-icons/fi';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import DealTimeline from '../../components/CRM/DealTimeline';
import CampaignLogsDrawer from './Campaigns/CampaignLogsDrawer';
import { COLOR_MAP as CAMPAIGN_NODE_COLOR_MAP } from '../../components/CRM/Campaigns/campaignConstants';
import { useAgentPage } from '../../components/FutureBot/FutureBotProvider';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import { useAnalyticsAgentTools } from './agent/useAnalyticsAgentTools';

// ============================================================================
// ERROR BOUNDARY - Prevents crashes from propagating and provides recovery UI
// ============================================================================

class AnalyticsErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Analytics Error Boundary caught error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Alert 
                    status="error" 
                    variant="subtle" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center"
                    textAlign="center"
                    p={6}
                    borderRadius="md"
                    bg="red.50"
                >
                    <AlertIcon boxSize="40px" mr={0} />
                    <Text mt={4} mb={1} fontSize="lg" fontWeight="bold" color="red.800">
                        {this.props.title || 'Something went wrong'}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        {this.props.message || 'Unable to display this section. The data may be temporarily unavailable.'}
                    </Text>
                    <Button 
                        leftIcon={<FiRefreshCw />} 
                        colorScheme="red" 
                        variant="outline" 
                        size="sm"
                        onClick={this.handleReset}
                    >
                        Try Again
                    </Button>
                </Alert>
            );
        }

        return this.props.children;
    }
}

// --- Constants ---
const DATE_RANGE_OPTIONS = [
    { value: 'last_week', label: 'Last Week' },
    { value: 'mtd', label: 'Month to Date' },
    { value: 'qtd', label: 'Quarter to Date' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'ytd', label: 'YTD' },
    { value: 'all_time', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
];

// Helper to calculate date ranges (all anchored to America/New_York)
const getDateRangeFromCode = (code, customStart, customEnd) => {
    const nowET = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const end = new Date(nowET);
    end.setHours(23, 59, 59, 999);
    const start = new Date(nowET);
    start.setHours(0, 0, 0, 0);
    
    switch (code) {
        case 'last_week': {
            // Previous full week: Sunday through Saturday in Eastern time
            const dayOfWeek = nowET.getDay(); // 0=Sun, 6=Sat
            const daysToLastSat = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
            const lastSat = new Date(nowET);
            lastSat.setDate(nowET.getDate() - daysToLastSat);
            lastSat.setHours(23, 59, 59, 999);
            const lastSun = new Date(lastSat);
            lastSun.setDate(lastSat.getDate() - 6);
            lastSun.setHours(0, 0, 0, 0);
            return { start: lastSun, end: lastSat };
        }
        case 'mtd':
            start.setDate(1);
            break;
        case 'qtd': {
            const quarter = Math.floor(start.getMonth() / 3);
            start.setMonth(quarter * 3, 1);
            break;
        }
        case 'custom':
            if (customStart && customEnd) {
                return { 
                    start: new Date(customStart + 'T00:00:00'),
                    end: new Date(customEnd + 'T23:59:59.999')
                };
            }
            start.setDate(end.getDate() - 30);
            break;
        case 'all_time':
            start.setFullYear(2025, 8, 1);
            break;
        case 'ytd':
            start.setFullYear(nowET.getFullYear(), 0, 1);
            break;
        default:
            start.setDate(end.getDate() - parseInt(code));
    }
    
    return { start, end };
};

// --- Components ---
const KpiCard = ({ label, value, subtext, trend, loading, icon, colorScheme = 'blue', onClick, clickable = false, tooltipLabel }) => {
    const content = (
        <Box 
            p={5} 
            shadow="sm" 
            borderWidth="1px" 
            borderRadius="md" 
            bg="white"
            cursor={clickable ? 'pointer' : 'default'}
            _hover={clickable ? { shadow: 'md', borderColor: `${colorScheme}.300` } : {}}
            onClick={onClick}
            transition="all 0.2s"
        >
            <Stat>
                <Flex align="center" gap={2}>
                    {icon && <Icon as={icon} color={`${colorScheme}.500`} boxSize={4} />}
                    <StatLabel color="gray.500" fontSize="xs" fontWeight="bold" textTransform="uppercase">
                        {label}
                        {tooltipLabel && (
                            <Tooltip label={tooltipLabel} placement="top">
                                <span><Icon as={FiInfo} ml={1} boxSize={3} color="gray.400" /></span>
                            </Tooltip>
                        )}
                    </StatLabel>
                </Flex>
                <Skeleton isLoaded={!loading} minH="28px" mt={1}>
                    <Flex align="center" gap={1}>
                        <StatNumber fontSize="2xl" fontWeight="bold" color={clickable ? `${colorScheme}.600` : 'inherit'}>
                            {value}
                        </StatNumber>
                        {clickable && <Icon as={FiExternalLink} color="gray.400" boxSize={3} />}
                    </Flex>
                </Skeleton>
                {subtext && (
                    <StatHelpText fontSize="xs" color="gray.400">
                        {trend !== undefined && trend !== null && <StatArrow type={trend > 0 ? 'increase' : 'decrease'} />}
                        {subtext}
                    </StatHelpText>
                )}
            </Stat>
        </Box>
    );
    
    return content;
};

// Fallback stage labels for stages not yet in CRM settings.
// Aligned 1:1 with Arive LOS as of 2026-05-20.
const PRETTY_STAGE_NAMES = {
    // Active Arive-aligned stages
    'lead-new':                  'New Lead',
    'lead-contacted':            'Contacted',
    'lead-quoted':               'Quoted',
    'loan-application-intake':   'App Intake',
    'loan-qualification':        'Qualification',
    'loan-preapproved':          'Pre-Approved',
    'loan-setup':                'Loan Setup',
    'loan-disclosure-sent':      'Disclosure Sent',
    'loan-submitted':            'UW Submitted',
    'loan-approved-conditions':  'Approved w/ Conditions',
    'loan-resubmittal':          'Re-Submittal',
    'loan-clear-to-close':       'Clear to Close',
    'loan-docs-out':             'Docs Out',
    'loan-docs-signed':          'Docs Signed',
    'loan-funded':               'Funded',
    'loan-brokercheck-received': 'Broker Check Received',
    'loan-finalized':            'Finalized',
    'deal-lost':                 'Lost',
    // Legacy slugs — historical labels
    'lead-qualified':            'Qualified (Legacy)',
    'lead-qualified-hot':        'Qualified Hot (Legacy)',
    'lead-contacted-hot':        'Contacted Hot (Legacy)',
    'lead-preapproved-legacy':   'Pre-Approved Lead (Legacy)',
    'lead-preapproved-shopper':  'Active Shopper (Legacy)',
    'loan-app-intake':           'App Intake (Legacy)',
    'loan-processing':           'Processing (Legacy)',
    'deal-won':                  'Funded (Legacy)',
};

// Funded stages (terminal-positive) — Arive-aligned + legacy slug.
const FUNDED_STAGES_ANALYTICS = ['loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-won'];
const CLOSED_STAGES_ANALYTICS = [...FUNDED_STAGES_ANALYTICS, 'deal-lost'];
// In-process loan workflow stages (active pipeline beyond app intake)
const PROCESSING_STAGES_ANALYTICS = [
    'loan-qualification', 'loan-preapproved',
    'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
    'loan-approved-conditions', 'loan-resubmittal',
    'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
    'loan-processing', // legacy
];
const APP_INTAKE_STAGES_ANALYTICS = ['loan-application-intake', 'loan-app-intake'];

const LEAD_DETAIL_COLUMNS = [
    { key: 'deal_name', label: 'Deal Name' },
    { key: 'source_medium', label: 'Source' },
    { key: 'stage', label: 'Stage' },
    { key: 'contacted', label: 'Contacted' },
    { key: 'conversation', label: 'Conversation' },
    { key: 'notes', label: 'Notes' },
    { key: 'owner_name', label: 'Owner' },
    { key: 'created_at', label: 'Created Date' },
    { key: 'first_contact', label: 'First Contact' },
    { key: 'stl_biz_minutes', label: 'STL Biz Hrs' },
];

const MONDAY_CARD_FILTERS = {
    total_leads:      { label: 'Total New Leads',   filterFn: () => true },
    contacted:        { label: 'Contacted Leads',   filterFn: d => d.contacted === 'Yes' },
    closings_period:  { label: 'Closings (Period)', source: 'periodClosingDeals' },
    lost_cohort:      { label: 'Lost Leads',        filterFn: d => d.stage === 'deal-lost' },
    conversations:    { label: 'Conversations',     filterFn: d => d.conversation === 'Yes' },
    active_leads:     { label: 'Active Leads',      filterFn: d => !CLOSED_STAGES_ANALYTICS.includes(d.stage) },
    in_app:           { label: 'In Application',    filterFn: d => APP_INTAKE_STAGES_ANALYTICS.includes(d.stage) },
    in_processing:    { label: 'In Processing',     filterFn: d => PROCESSING_STAGES_ANALYTICS.includes(d.stage) },
};

// Campaigns tab: category labels/colors (aligned with CampaignAnalytics.jsx)
const CAMPAIGN_CATEGORY_LABELS = {
    'lead-conversion': 'Lead Conversion',
    'in-process': 'In-Process',
    'post-close': 'Post-Close',
    'partner': 'Partner Management',
    'custom': 'Custom',
};

const CAMPAIGN_CATEGORY_COLORS = {
    'lead-conversion': 'blue',
    'in-process': 'green',
    'post-close': 'purple',
    'partner': 'orange',
    'custom': 'gray',
};

// Campaigns tab: sortable table columns (key = field on the campaign row)
const CAMPAIGN_TABLE_COLUMNS = [
    { key: 'name', label: 'Campaign', numeric: false },
    { key: 'category', label: 'Category', numeric: false },
    { key: 'trigger_type', label: 'Trigger', numeric: false },
    { key: 'steps', label: 'Steps', numeric: true },
    { key: 'active_subscribers', label: 'Runners', numeric: true },
    { key: 'enrolled', label: 'Enrolled', numeric: true },
    { key: 'active_now', label: 'Active', numeric: true },
    { key: 'completed', label: 'Completed', numeric: true },
    { key: 'failed', label: 'Failed', numeric: true },
    { key: 'success_rate', label: 'Success %', numeric: true },
    { key: 'emails_sent', label: 'Emails', numeric: true },
    { key: 'open_rate', label: 'Open %', numeric: true },
    { key: 'click_rate', label: 'Click %', numeric: true },
    { key: 'sms_sent', label: 'SMS', numeric: true },
    { key: 'funded_volume', label: 'Funded', numeric: true },
    { key: 'last_activity_at', label: 'Last Activity', numeric: false },
];

/** 'PIPELINE_STAGE_CHANGED' -> 'Pipeline Stage Changed' */
function formatTriggerLabel(triggerType) {
    if (!triggerType) return '\u2014';
    return String(triggerType)
        .toLowerCase()
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/**
 * MultiSelectFilterDropdown - Searchable multi-select dropdown for filters
 * Similar to TagsFilterDropdown in ContactsFilterToolbar
 * 
 * IMPORTANT: This component only triggers onApply when the menu closes,
 * preventing multiple API calls while the user is making selections.
 */
// Memoized option row: with a stable onToggle, clicking a checkbox re-renders
// only the affected row instead of the entire option list.
const FilterOptionRow = memo(function FilterOptionRow({ opt, isSelected, colorScheme, onToggle }) {
    return (
        <MenuItem
            onClick={() => onToggle(opt)}
            bg={isSelected ? `${colorScheme}.50` : undefined}
            _hover={{ bg: isSelected ? `${colorScheme}.100` : 'gray.100' }}
        >
            <HStack spacing={2} width="100%">
                <Checkbox 
                    isChecked={isSelected} 
                    pointerEvents="none"
                    size="sm"
                    colorScheme={colorScheme}
                />
                <Text fontSize="sm">{opt.label}</Text>
            </HStack>
        </MenuItem>
    );
});

function MultiSelectFilterDropdown({ 
    label, 
    icon, 
    colorScheme = 'blue',
    options = [], 
    committedValues = [],   // the applied filter ({value,label}[]); empty = no filter (All)
    onApply,                // called once, on menu close, when the selection changed
    isLoading = false 
}) {
    // Selection edits are drafted locally while the menu is open and committed
    // once on close, so checkbox clicks never re-render the whole page.
    const [draft, setDraft] = useState(committedValues);
    const [searchTerm, setSearchTerm] = useState('');
    // Option order is frozen per menu-open (committed selection floats to the
    // top) so rows don't jump around under the cursor as they are toggled.
    const [orderedOptions, setOrderedOptions] = useState(options);
    const [isOpen, setIsOpen] = useState(false);

    const handleOpen = () => {
        const committedSet = new Set(committedValues.map(v => v.value));
        setOrderedOptions([
            ...options.filter(opt => committedSet.has(opt.value)),
            ...options.filter(opt => !committedSet.has(opt.value)),
        ]);
        setDraft(committedValues);
        setSearchTerm('');
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        const committedSet = new Set(committedValues.map(v => v.value));
        const changed = draft.length !== committedValues.length ||
            draft.some(opt => !committedSet.has(opt.value));
        if (changed && onApply) {
            onApply(draft);
        }
    };

    const handleToggle = useCallback((opt) => {
        setDraft(prev => prev.some(v => v.value === opt.value)
            ? prev.filter(v => v.value !== opt.value)
            : [...prev, opt]);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return orderedOptions;
        const q = searchTerm.toLowerCase();
        return orderedOptions.filter(opt =>
            opt.label.toLowerCase().includes(q) ||
            opt.value.toLowerCase().includes(q)
        );
    }, [orderedOptions, searchTerm]);

    const draftValueSet = useMemo(() => new Set(draft.map(v => v.value)), [draft]);

    // While open the button mirrors the draft; while closed, the committed filter.
    const current = isOpen ? draft : committedValues;
    const selectedCount = current.length;
    const allSelected = selectedCount === options.length && options.length > 0;
    const noneSelected = selectedCount === 0;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
        <Menu closeOnSelect={false} isLazy onOpen={handleOpen} onClose={handleClose}>
            <MenuButton
                as={Button}
                size="sm"
                variant={someSelected ? 'solid' : 'outline'}
                colorScheme={someSelected ? colorScheme : 'gray'}
                leftIcon={<Icon as={icon} />}
                rightIcon={<FiChevronDown />}
                isLoading={isLoading}
                bg={someSelected ? undefined : 'white'}
            >
                {label}
                {(noneSelected || allSelected) ? ': All' : ''}
                {someSelected && (
                    <Badge ml={1} colorScheme="white" bg={`${colorScheme}.600`} color="white" fontSize="xs">
                        {selectedCount}
                    </Badge>
                )}
            </MenuButton>
            <Portal>
                <MenuList zIndex={1500} maxH="400px" overflowY="auto" minW="220px">
                    {/* Search Input */}
                    <Box px={3} py={2}>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={FiSearch} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder={`Search ${label.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </InputGroup>
                    </Box>
                    
                    {noneSelected && (
                        <Box px={3} pb={1}>
                            <Text fontSize="xs" color="gray.500">
                                Showing all — check to filter
                            </Text>
                        </Box>
                    )}
                    
                    {/* Select All / Clear All Buttons */}
                    <HStack px={3} py={2} spacing={2}>
                        {!allSelected && (
                            <Button 
                                size="xs" 
                                variant="outline" 
                                colorScheme={colorScheme}
                                onClick={() => setDraft([...options])}
                                flex={1}
                            >
                                Select All
                            </Button>
                        )}
                        {!noneSelected && (
                            <Button 
                                size="xs" 
                                variant="outline" 
                                colorScheme="gray"
                                onClick={() => setDraft([])}
                                flex={1}
                            >
                                Clear All
                            </Button>
                        )}
                    </HStack>
                    
                    <MenuDivider my={1} />
                    
                    {/* Options List */}
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <FilterOptionRow
                                key={opt.value}
                                opt={opt}
                                isSelected={draftValueSet.has(opt.value)}
                                colorScheme={colorScheme}
                                onToggle={handleToggle}
                            />
                        ))
                    ) : (
                        <MenuItem isDisabled>
                            <Text fontSize="sm" color="gray.500">No options found</Text>
                        </MenuItem>
                    )}
                    
                    {/* Selection Summary */}
                    {selectedCount > 0 && (
                        <>
                            <MenuDivider my={1} />
                            <Box px={3} py={2}>
                                <Badge colorScheme={colorScheme}>
                                    {allSelected ? 'All selected' : `${selectedCount} of ${options.length} selected`}
                                </Badge>
                            </Box>
                        </>
                    )}
                </MenuList>
            </Portal>
        </Menu>
    );
}

// ============================================================================
// DATA SANITIZATION UTILITIES - Prevent DecimalError crashes from undefined values
// ============================================================================

// Default schemas for each data type - ensures all fields have safe defaults
const SUMMARY_SCHEMA = {
    total_leads: 0, won_count: 0, lost_count: 0, active_pipeline: 0,
    active_leads: 0, avg_deal_value: 0, avg_close_days: 0,
    avg_response_minutes: 0, conversion_rate: 0
};

const ACTIVITY_SUMMARY_SCHEMA = {
    total_deals_touched: 0, avg_deals_touched_per_day: 0,
    total_call_dispositions: 0, total_quotes: 0, total_emails: 0,
    total_meetings: 0, active_users: 0, active_days: 0, total_activities: 0
};

const TASK_HEALTH_SCHEMA = {
    total_active_deals: 0, deals_without_tasks: 0, deals_with_overdue: 0,
    total_tasks: 0, total_overdue_tasks: 0, avg_tasks_per_deal: 0,
    pct_no_tasks: 0, pct_with_overdue: 0
};

const MONDAY_REPORT_TOTALS_SCHEMA = {
    total_leads: 0, avg_stl: 0, avg_stl_biz: 0, active_leads: 0,
    leads_with_conv: 0, conv_pct: 0, leads_contacted: 0, contacted_pct: 0,
    leads_lost_cohort: 0, lost_cohort_pct: 0, lost_period: 0,
    in_app: 0, pre_approved: 0, in_processing: 0, closings_period: 0
};

const CAMPAIGN_SUMMARY_SCHEMA = {
    total_campaigns: 0, active_campaigns: 0,
    enrolled: 0, active_now: 0, completed: 0, failed: 0, cancelled: 0,
    completion_rate: 0, steps_executed: 0,
    emails_sent: 0, unique_opens: 0, open_rate: 0, unique_clicks: 0, click_rate: 0,
    sms_sent: 0, sms_failed: 0, funded_deals: 0, funded_volume: 0
};

// Deep sanitization for summary/single-object data
const sanitizeSummaryObject = (obj, schema) => {
    if (!obj || typeof obj !== 'object') return { ...schema };
    const result = { ...schema };
    Object.keys(schema).forEach(key => {
        const val = obj[key];
        if (typeof schema[key] === 'number') {
            const num = Number(val);
            result[key] = Number.isFinite(num) ? num : schema[key];
        } else if (typeof schema[key] === 'string') {
            result[key] = val ?? schema[key];
        } else {
            result[key] = val ?? schema[key];
        }
    });
    return result;
};

// Safe reduce wrapper - prevents crashes from malformed arrays
const safeReduce = (arr, reducer, initial) => {
    if (!Array.isArray(arr) || arr.length === 0) return initial;
    try {
        return arr.reduce(reducer, initial);
    } catch (e) {
        console.error('safeReduce error:', e);
        return initial;
    }
};

// Safe number formatter - ensures toFixed/toLocaleString never receives undefined
const safeNumber = (val, defaultVal = 0) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : defaultVal;
};

// Data sanitizer utility to prevent Recharts errors
const sanitizeNumericData = (data, numericFields, stringFields = []) => {
    if (!Array.isArray(data)) return [];
    return data
        .filter(item => item !== null && item !== undefined && typeof item === 'object')
        .map(item => {
            const cleaned = {};
            // Copy and validate numeric fields
            numericFields.forEach(field => {
                const val = item[field];
                const num = Number(val);
                cleaned[field] = Number.isFinite(num) ? num : 0;
            });
            // Copy string fields with fallback
            stringFields.forEach(field => {
                cleaned[field] = item[field] ?? '';
            });
            // Copy remaining fields (preserves user_name, deal_id, etc.)
            Object.keys(item).forEach(key => {
                if (!(key in cleaned)) {
                    cleaned[key] = item[key];
                }
            });
            return cleaned;
        });
};

// Sanitize chart data specifically - filter out entries with missing category keys
const sanitizeChartData = (data, categoryKey, numericFields) => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data
        .filter(item => item !== null && item !== undefined && item[categoryKey] !== null && item[categoryKey] !== undefined && item[categoryKey] !== '')
        .map(item => {
            const cleaned = { ...item };
            numericFields.forEach(field => {
                const val = item[field];
                if (val === undefined || val === null || val === '' || Number.isNaN(Number(val)) || !Number.isFinite(Number(val))) {
                    cleaned[field] = 0;
                } else {
                    cleaned[field] = Number(val);
                }
            });
            return cleaned;
        });
};

// Check if chart data has any valid numeric values (not all zeros)
const hasValidChartData = (data, numericFields) => {
    if (!Array.isArray(data) || data.length === 0) return false;
    // Must have at least one item with valid data
    const hasValidItem = data.some(item => {
        if (!item || typeof item !== 'object') return false;
        return numericFields.some(field => {
            const val = Number(item[field]);
            return Number.isFinite(val) && val >= 0;
        });
    });
    return hasValidItem;
};

// Calculate safe domain max for charts
const getChartDomainMax = (data, fields) => {
    if (!Array.isArray(data) || data.length === 0) return 10;
    let max = 0;
    data.forEach(item => {
        fields.forEach(field => {
            const val = Number(item[field]);
            // Use Number.isFinite() to exclude NaN and Infinity
            if (Number.isFinite(val) && val > max) {
                max = val;
            }
        });
    });
    // Return at least 1 to prevent zero domain issues. Also ensure it's a finite number.
    const result = Math.max(max, 1);
    // Force a safe integer to avoid float precision issues in tick generation
    return Number.isFinite(result) ? Math.ceil(result) : 10;
};

// ============================================================================
// SOURCE DISPLAY LABELS - Maps raw source_medium values to human-readable names
// Only affects how labels appear in the UI; does NOT change how sources are loaded
// ============================================================================
const SOURCE_DISPLAY_LABELS = {
    'google-ads-dscr': 'Google Ads - DSCR',
    'website-organic': 'Website - Organic',
    'webhook-other': 'Webhook - Other',
    'webhook_with_no_source_provided': 'Webhook (Legacy)',
    'webhook': 'Webhook (Legacy)',
    'self-gen': 'Self-Generated',
    'manual': 'Manual Entry',
    'import': 'CSV Import',
    'hub-submission': 'Hub Submission',
    'google': 'Google',
    'aivo': 'AIVO',
    'portfolio': 'Past Client',
    'organic': 'Organic',
    'spend': 'Spend',
    'referral': 'Referral',
    'zillow': 'Zillow',
    'realtor': 'Realtor.com',
    'boomtown': 'BoomTown',
    'myhomeiq': 'MyHomeIQ',
};

/**
 * Returns a display label for a raw source_medium value.
 * Checks the lookup map first, then falls back to Title Case formatting.
 */
function formatSourceLabel(raw) {
    if (SOURCE_DISPLAY_LABELS[raw]) return SOURCE_DISPLAY_LABELS[raw];
    // Fallback: capitalize each word, replace hyphens/underscores with spaces
    return raw
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Sentinel for the "(No tags)" option in the Tags filter.
 *
 * 43% of deals carry no tags, so without an explicit way to include them,
 * narrowing the tag filter at all would silently drop them from every metric.
 * The sentinel rides inside the tags array rather than being a separate flag so
 * one field stays unambiguous: [] means "all", ['__NO_TAGS__'] means "only
 * untagged". Verified against live data — no real tag value collides with it.
 */
const NO_TAGS_VALUE = '__NO_TAGS__';

/**
 * Tags payload for the backend, which reads an empty array as "no filter".
 *
 * Shared by the batch fetch, the drill-downs and the Monday-report email so the
 * all-or-none rule can't drift between them. None-selected is treated as all,
 * matching what the stage and source filters already do when cleared.
 */
function buildTagsPayload(committedTags, tagOptionCount) {
    const isAllOrNone = committedTags.length === tagOptionCount || committedTags.length === 0;
    return isAllOrNone ? [] : committedTags.map(t => t.value);
}

// ============================================================================
// URL PARAMETER PARSING - Reads filter state from URL query string
// ============================================================================

function parseUrlParams(search) {
    const params = new URLSearchParams(search);
    return {
        range: params.get('range'),           // e.g. '7', 'mtd', 'custom'
        start: params.get('start'),           // e.g. '2025-12-01'
        end: params.get('end'),               // e.g. '2026-01-31'
        users: params.get('users'),           // e.g. 'uid1,uid2' or null
        stages: params.get('stages'),         // e.g. 'lead-new,loan-app-intake' or null
        sources: params.get('sources'),       // e.g. 'zillow,realtor' or null
        tags: params.get('tags'),             // e.g. 'Past Client,retr' or null
        tab: params.get('tab'),               // e.g. '2' or null
    };
}

const AnalyticsPage = () => {
    // --- Auth ---
    const { currentUser, SuperAdmin, Roles } = useAuth();
    const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
    const toast = useToast();
    
    // --- CRM Settings (for deal stages) ---
    const { dealStages, stagesLoading, dispositions } = useCrmSettings();
    
    // --- URL State Sync ---
    const history = useHistory();
    const location = useLocation();
    // Parse URL params once on mount (useMemo with [] ensures single read)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialParams = useMemo(() => parseUrlParams(location.search), []);
    
    // Refs to hold raw URL param strings for deferred reconciliation
    // (users/stages/sources/tags can't be resolved until option lists load)
    const urlUsersRef = useRef(initialParams.users);
    const urlStagesRef = useRef(initialParams.stages);
    const urlSourcesRef = useRef(initialParams.sources);
    const urlTagsRef = useRef(initialParams.tags);
    
    // Helper to get stage label - uses dealStages from CRM settings, falls back to PRETTY_STAGE_NAMES
    const getStageLabel = (stageValue) => {
        if (!stageValue) return 'Unknown';
        const stageConfig = dealStages?.find(s => s.value === stageValue);
        return stageConfig?.label || PRETTY_STAGE_NAMES[stageValue] || stageValue;
    };
    
    // --- State ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRangeCode, setDateRangeCode] = useState(
        initialParams.range && DATE_RANGE_OPTIONS.some(o => o.value === initialParams.range)
            ? initialParams.range
            : 'last_week'
    );
    
    // Custom date range
    const [customStartDate, setCustomStartDate] = useState(initialParams.start || '');
    const [customEndDate, setCustomEndDate] = useState(initialParams.end || '');
    
    // Tab index (controlled) - initialized from URL or defaults to 0 (Overview)
    const [tabIndex, setTabIndex] = useState(
        initialParams.tab !== null ? Math.min(Math.max(parseInt(initialParams.tab, 10) || 0, 0), 5) : 0
    );
    
    // Filters — the dropdowns draft their selection internally and commit it on
    // menu close. Empty committed state means "no filter" (All) for every
    // dropdown, matching the backend convention (empty array = no constraint).
    const [users, setUsers] = useState([]);
    const [committedUsers, setCommittedUsers] = useState([]);
    
    // Stage/Source/Tag filter options (loaded dynamically from Supabase)
    const [stageOptions, setStageOptions] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [committedStages, setCommittedStages] = useState([]);
    const [committedSourceMediums, setCommittedSourceMediums] = useState([]);
    const [committedTags, setCommittedTags] = useState([]);
    const [filtersLoaded, setFiltersLoaded] = useState(false);

    // Existing Data
    const [summary, setSummary] = useState({});
    const [volumeData, setVolumeData] = useState([]);
    const [funnelData, setFunnelData] = useState([]);
    const [loStats, setLoStats] = useState([]);
    
    // Advanced Analytics Data
    const [userActivityData, setUserActivityData] = useState([]);
    const [taskAnalyticsData, setTaskAnalyticsData] = useState([]);
    const [taskHealthSummary, setTaskHealthSummary] = useState({});
    const [activitySummary, setActivitySummary] = useState({});
    
    // NEW: Stagnant leads, no-followup, speed to lead, appointments
    const [stagnantLeads, setStagnantLeads] = useState([]);
    const [leadsWithoutFollowup, setLeadsWithoutFollowup] = useState([]);
    const [speedToLeadData, setSpeedToLeadData] = useState([]);
    const [appointmentsAppsData, setAppointmentsAppsData] = useState([]);
    
    // Monday Report Data (per-user metrics combining cohort, period, and snapshot data)
    const [mondayReportData, setMondayReportData] = useState({ byUser: [], totals: {}, deals: [], periodClosingDeals: [] });
    const [mondayDetailSearch, setMondayDetailSearch] = useState('');
    const [mondayDetailSort, setMondayDetailSort] = useState({ key: 'created_at', dir: 'desc' });
    
    // Monday Report card drilldown
    const { isOpen: isMondayCardModalOpen, onOpen: onMondayCardModalOpen, onClose: onMondayCardModalClose } = useDisclosure();
    const [mondayCardFilterKey, setMondayCardFilterKey] = useState(null);
    const [mondayCardSearch, setMondayCardSearch] = useState('');
    const [mondayCardSort, setMondayCardSort] = useState({ key: 'created_at', dir: 'desc' });

    // Campaigns tab data (batch_campaigns report) + table UI state
    const [campaignAnalytics, setCampaignAnalytics] = useState({ summary: {}, campaigns: [] });
    const [campaignSearch, setCampaignSearch] = useState('');
    const [campaignSort, setCampaignSort] = useState({ key: 'enrolled', dir: 'desc' });

    // Campaign detail drill-down modal (campaign_detail report)
    const { isOpen: isCampaignDetailOpen, onOpen: onCampaignDetailOpen, onClose: onCampaignDetailClose } = useDisclosure();
    const [campaignDetail, setCampaignDetail] = useState(null);
    const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);
    const [campaignDetailTarget, setCampaignDetailTarget] = useState(null); // { templateId, name }

    // Campaign contact-logs drawer (reuses CampaignLogsDrawer, client-side fetch)
    const { isOpen: isCampaignLogsOpen, onOpen: onCampaignLogsOpen, onClose: onCampaignLogsClose } = useDisclosure();
    const [campaignLogsTarget, setCampaignLogsTarget] = useState(null); // { templateId, name }

    // Drill-down modal state
    const { isOpen: isOverdueModalOpen, onOpen: onOverdueModalOpen, onClose: onOverdueModalClose } = useDisclosure();
    const { isOpen: isStagnantModalOpen, onOpen: onStagnantModalOpen, onClose: onStagnantModalClose } = useDisclosure();
    const { isOpen: isNoFollowupModalOpen, onOpen: onNoFollowupModalOpen, onClose: onNoFollowupModalClose } = useDisclosure();
    const { isOpen: isDealsWithoutTasksModalOpen, onOpen: onDealsWithoutTasksModalOpen, onClose: onDealsWithoutTasksModalClose } = useDisclosure();
    const { isOpen: isDealsWithOverdueModalOpen, onOpen: onDealsWithOverdueModalOpen, onClose: onDealsWithOverdueModalClose } = useDisclosure();
    const [drillDownData, setDrillDownData] = useState([]);
    const [drillDownLoading, setDrillDownLoading] = useState(false);
    const [drillDownTitle, setDrillDownTitle] = useState('');
    
    // Monday Report email state
    const [sendingEmail, setSendingEmail] = useState(false);
    
    // Notes modal state (Lead Detail activity timeline popup)
    const { isOpen: isNotesModalOpen, onOpen: onNotesModalOpen, onClose: onNotesModalClose } = useDisclosure();
    const [notesModalDealName, setNotesModalDealName] = useState('');
    const [notesModalActivities, setNotesModalActivities] = useState([]);
    const [notesModalLoading, setNotesModalLoading] = useState(false);
    
    // Refresh trigger
    const [refreshKey, setRefreshKey] = useState(0);

    // Refs
    const dashboardRef = useRef(null);
    
    // Response cache: Map<cacheKey, { data, timestamp }> with 5-minute TTL
    const responseCacheRef = useRef(new Map());
    const CACHE_TTL_MS = 5 * 60 * 1000;

    // --- Copy Monday Report to Clipboard (rich HTML for Gmail) ---
    const copyMondayReportToClipboard = async () => {
        const t = mondayReportData.totals || {};
        const users = mondayReportData.byUser || [];
        const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
        const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const dateLabel = fmtDate(start) + ' \u2013 ' + fmtDate(end);
        const sn = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

        // Helper: build a KPI cell with width percentage for even distribution
        const kpiCell = (label, value, widthPct) => {
            return '<td style="padding:16px 8px;text-align:center;vertical-align:top;width:' + widthPct + '%;">' +
                '<div style="font-size:24px;font-weight:700;color:#1a202c;line-height:1.2;">' + value + '</div>' +
                '<div style="font-size:10px;font-weight:600;color:#718096;text-transform:uppercase;letter-spacing:0.5px;margin-top:4px;">' + label + '</div>' +
                '</td>';
        };

        // Helper: build a data cell
        const tdCell = (val, color, bold) => {
            var style = 'padding:8px;text-align:center;border-bottom:1px solid #edf2f7;';
            if (color) style += 'color:' + color + ';';
            if (bold) style += 'font-weight:700;';
            return '<td style="' + style + '">' + val + '</td>';
        };

        // Helper: build a totals cell
        const totCell = (val, color) => {
            var style = 'padding:10px 8px;text-align:center;border-top:2px solid #cbd5e0;';
            if (color) style += 'color:' + color + ';';
            return '<td style="' + style + '">' + val + '</td>';
        };

        // TH style (reusable)
        const thStyle = 'padding:10px 8px;text-align:center;color:#ffffff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #2c5282;';

        // Build user rows
        const userRows = users.map(function(u, i) {
            const bg = i % 2 === 0 ? '#ffffff' : '#f7fafc';
            const stlVal = sn(u.avg_stl_biz);
            const stlColor = stlVal <= 30 ? '#38a169' : stlVal <= 60 ? '#d69e2e' : '#e53e3e';
            const convColor = sn(u.conv_pct) >= 20 ? '#38a169' : sn(u.conv_pct) >= 10 ? '#d69e2e' : '#e53e3e';
            const contactColor = sn(u.contacted_pct) >= 80 ? '#38a169' : sn(u.contacted_pct) >= 50 ? '#d69e2e' : '#e53e3e';
            const lostColor = sn(u.lost_cohort_pct) > 30 ? '#e53e3e' : '#1a202c';

            return '<tr style="background:' + bg + ';">' +
                '<td style="padding:8px 12px;text-align:left;border-bottom:1px solid #edf2f7;font-weight:600;">' + (u.user_name || 'Unknown') + '</td>' +
                tdCell(sn(u.total_leads), null, true) +
                tdCell(stlVal > 0 ? stlVal.toFixed(0) : '\u2014', stlVal > 0 ? stlColor : '#a0aec0', false) +
                tdCell(sn(u.active_leads), null, false) +
                tdCell(sn(u.leads_with_conv), null, false) +
                tdCell(sn(u.conv_pct).toFixed(1) + '%', convColor, false) +
                tdCell(sn(u.leads_contacted), null, false) +
                tdCell(sn(u.contacted_pct).toFixed(1) + '%', contactColor, false) +
                tdCell(sn(u.leads_lost_cohort), null, false) +
                tdCell(sn(u.lost_cohort_pct).toFixed(1) + '%', lostColor, false) +
                tdCell(sn(u.in_app), null, false) +
                tdCell(sn(u.pre_approved), null, false) +
                tdCell(sn(u.in_processing), null, false) +
                tdCell(sn(u.closings_period), '#38a169', true) +
                '</tr>';
        }).join('');

        // Build totals row
        const totalsRow = '<tr style="background:#edf2f7;font-weight:700;">' +
            '<td style="padding:10px 12px;text-align:left;border-top:2px solid #cbd5e0;font-size:12px;font-weight:700;">TEAM TOTAL</td>' +
            totCell(sn(t.total_leads)) +
            totCell(sn(t.avg_stl_biz).toFixed(0)) +
            totCell(sn(t.active_leads)) +
            totCell(sn(t.leads_with_conv)) +
            totCell(sn(t.conv_pct).toFixed(1) + '%') +
            totCell(sn(t.leads_contacted)) +
            totCell(sn(t.contacted_pct).toFixed(1) + '%') +
            totCell(sn(t.leads_lost_cohort)) +
            totCell(sn(t.lost_cohort_pct).toFixed(1) + '%') +
            totCell(sn(t.in_app)) +
            totCell(sn(t.pre_approved)) +
            totCell(sn(t.in_processing)) +
            totCell(sn(t.closings_period), '#38a169') +
            '</tr>';

        const nowStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

        // Assemble full HTML
        const html = [
            '<div style="font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif;max-width:900px;color:#1a202c;">',

            // Header
            '<table width="100%" cellpadding="0" cellspacing="0" style="border:none;">',
            '<tr><td style="padding:16px 20px;background:#2b6cb0;border-radius:8px 8px 0 0;">',
            '<div style="font-size:20px;font-weight:700;color:#ffffff;margin:0;">Monday Report</div>',
            '<div style="font-size:13px;color:#bee3f8;margin-top:2px;">' + dateLabel + '</div>',
            '</td></tr></table>',

            // KPI Row 1 (5 cells, 20% each)
            '<table width="100%" cellpadding="0" cellspacing="0" style="table-layout:fixed;border-left:1px solid #e2e8f0;border-right:1px solid #e2e8f0;background:#f7fafc;">',
            '<tr>',
            kpiCell('Total New Leads', sn(t.total_leads), 20),
            kpiCell('Avg STL (Biz Hrs)', sn(t.avg_stl_biz).toFixed(0) + ' min', 20),
            kpiCell('Contact Rate', sn(t.contacted_pct).toFixed(1) + '%', 20),
            kpiCell('Closings', sn(t.closings_period), 20),
            kpiCell('Lost', sn(t.leads_lost_cohort) + ' (' + sn(t.lost_cohort_pct).toFixed(1) + '%)', 20),
            '</tr></table>',

            // KPI Row 2 (4 cells, 25% each)
            '<table width="100%" cellpadding="0" cellspacing="0" style="table-layout:fixed;border-left:1px solid #e2e8f0;border-right:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0;background:#f7fafc;">',
            '<tr>',
            kpiCell('Conversations', sn(t.leads_with_conv) + ' (' + sn(t.conv_pct).toFixed(1) + '%)', 25),
            kpiCell('Active Leads', sn(t.active_leads), 25),
            kpiCell('In Application', sn(t.in_app), 25),
            kpiCell('In Processing', sn(t.in_processing), 25),
            '</tr></table>',

            // Spacer
            '<div style="height:16px;"></div>',

            // Data Table
            '<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e2e8f0;font-size:12px;">',
            '<thead><tr style="background:#2b6cb0;">',
            '<th style="padding:10px 12px;text-align:left;color:#ffffff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #2c5282;">User</th>',
            '<th style="' + thStyle + '">New Leads</th>',
            '<th style="' + thStyle + '">STL Biz</th>',
            '<th style="' + thStyle + '">Active</th>',
            '<th style="' + thStyle + '">Conv</th>',
            '<th style="' + thStyle + '">Conv %</th>',
            '<th style="' + thStyle + '">Contacted</th>',
            '<th style="' + thStyle + '">Contact %</th>',
            '<th style="' + thStyle + '">Lost</th>',
            '<th style="' + thStyle + '">Lost %</th>',
            '<th style="' + thStyle + '">In App</th>',
            '<th style="' + thStyle + '">Pre-App</th>',
            '<th style="' + thStyle + '">Processing</th>',
            '<th style="' + thStyle + '">Closings</th>',
            '</tr></thead>',
            '<tbody>',
            userRows,
            totalsRow,
            '</tbody></table>',

            // Footer
            '<div style="margin-top:12px;font-size:11px;color:#a0aec0;text-align:right;">',
            'Generated from Origin CRM \u00b7 ' + nowStr,
            '</div>',

            '</div>',
        ].join('\n');

        // Plain-text fallback
        const divider = '\u2500'.repeat(50);
        const plainLines = [
            'MONDAY REPORT \u2014 ' + dateLabel,
            divider,
            'Total New Leads: ' + sn(t.total_leads) + '  |  Avg STL (Biz): ' + sn(t.avg_stl_biz).toFixed(0) + ' min  |  Contact Rate: ' + sn(t.contacted_pct).toFixed(1) + '%  |  Closings: ' + sn(t.closings_period) + '  |  Lost: ' + sn(t.leads_lost_cohort) + ' (' + sn(t.lost_cohort_pct).toFixed(1) + '%)',
            'Conversations: ' + sn(t.leads_with_conv) + ' (' + sn(t.conv_pct).toFixed(1) + '%)  |  Active: ' + sn(t.active_leads) + '  |  In App: ' + sn(t.in_app) + '  |  Processing: ' + sn(t.in_processing),
            '',
            divider,
        ];
        users.forEach(function(u) {
            plainLines.push(
                (u.user_name || 'Unknown').padEnd(20) + '  Leads: ' + sn(u.total_leads) + '  STL: ' + (sn(u.avg_stl_biz) > 0 ? sn(u.avg_stl_biz).toFixed(0) : '\u2014') + '  Active: ' + sn(u.active_leads) + '  Conv: ' + sn(u.conv_pct).toFixed(1) + '%  Contact: ' + sn(u.contacted_pct).toFixed(1) + '%  Lost: ' + sn(u.lost_cohort_pct).toFixed(1) + '%  App: ' + sn(u.in_app) + '  Proc: ' + sn(u.in_processing) + '  Closings: ' + sn(u.closings_period)
            );
        });
        const plain = plainLines.join('\n');

        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([plain], { type: 'text/plain' }),
                })
            ]);
            toast({
                title: 'Copied to clipboard!',
                description: 'Monday Report is ready to paste in Gmail.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (err) {
            console.error('Clipboard write failed:', err);
            try {
                await navigator.clipboard.writeText(plain);
                toast({
                    title: 'Copied as plain text',
                    description: 'Rich formatting was unavailable. Plain text copied instead.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                });
            } catch (fallbackErr) {
                toast({
                    title: 'Copy failed',
                    description: 'Unable to access clipboard. Please try again.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'top',
                });
            }
        }
    };

    // --- Send Monday Report Email (on-demand via Firebase callable) ---
    const sendMondayReportEmailHandler = async () => {
        setSendingEmail(true);
        try {
            const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
            const userIds = committedUsers ? committedUsers.map(u => u.value) : [];
            const stages = committedStages ? committedStages.map(s => s.value) : [];
            const sourceMediums = committedSourceMediums ? committedSourceMediums.map(s => s.value) : [];
            const tags = buildTagsPayload(committedTags, tagOptions.length);

            const sendFn = httpsCallable(functions, 'sendMondayReportEmail');
            const result = await sendFn({
                dateRange: { start: start.toISOString ? start.toISOString() : new Date(start).toISOString(), end: end.toISOString ? end.toISOString() : new Date(end).toISOString() },
                filters: { userIds, stages, sourceMediums, tags }
            });

            toast({
                title: 'Email sent!',
                description: 'Monday Report emailed to mondayreport@future.loans',
                status: 'success',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } catch (err) {
            console.error('Send Monday Report email failed:', err);
            toast({
                title: 'Email failed',
                description: err.message || 'Unable to send the Monday Report email. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setSendingEmail(false);
        }
    };

    // Chart ready state - delays chart rendering to avoid Recharts transition errors
    const [chartReady, setChartReady] = useState(false);
    
    // Reset chart ready state when loading starts, set it after data loads
    useEffect(() => {
        if (loading) {
            setChartReady(false);
        } else {
            // Small delay to let data settle before rendering chart
            const timer = setTimeout(() => setChartReady(true), 100);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    // Safe chart data - ensures Recharts always gets valid data
    // Uses sourceOptions labels for consistent display with the Sources filter
    const safeVolumeData = useMemo(() => {
        if (!Array.isArray(volumeData) || volumeData.length === 0) return [];
        return volumeData.filter(item => 
            item && 
            typeof item === 'object' && 
            item.source && 
            Number.isFinite(Number(item.total_leads)) &&
            Number.isFinite(Number(item.funded_count))
        ).map(item => {
            // Find matching source option to use its label
            const sourceOpt = sourceOptions.find(s => s.value === item.source);
            return {
                ...item,
                // Use the formatted label from sourceOptions if available
                source: sourceOpt?.label || item.source,
                total_leads: Number(item.total_leads) || 0,
                funded_count: Number(item.funded_count) || 0
            };
        });
    }, [volumeData, sourceOptions]);

    // Known top-level keys on deal detail rows (everything else is a disposition key)
    const DEAL_DETAIL_STATIC_KEYS = useMemo(() => new Set([
        'deal_id', 'deal_name', 'source_medium', 'stage', 'contacted',
        'conversation', 'owner_name', 'owner_id', 'created_at', 'first_contact',
        'stl_biz_minutes', 'form_name', 'disposition_counts'
    ]), []);

    // Monday Report detail table: filtered + sorted deals
    const mondayDetailDeals = useMemo(() => {
        let rows = mondayReportData.deals || [];
        // Text search filter
        if (mondayDetailSearch.trim()) {
            const q = mondayDetailSearch.trim().toLowerCase();
            rows = rows.filter(d =>
                (d.deal_name || '').toLowerCase().includes(q) ||
                (d.source_medium || '').toLowerCase().includes(q) ||
                (d.owner_name || '').toLowerCase().includes(q) ||
                (d.stage || '').toLowerCase().includes(q) ||
                (d.form_name || '').toLowerCase().includes(q)
            );
        }
        // Sort
        const { key, dir } = mondayDetailSort;
        const isDispositionKey = !DEAL_DETAIL_STATIC_KEYS.has(key);
        const sorted = [...rows].sort((a, b) => {
            let aVal, bVal;
            if (isDispositionKey) {
                aVal = (a.disposition_counts?.[key] || 0);
                bVal = (b.disposition_counts?.[key] || 0);
            } else if (key === 'created_at' || key === 'first_contact') {
                aVal = a[key] ? new Date(a[key]).getTime() : 0;
                bVal = b[key] ? new Date(b[key]).getTime() : 0;
            } else if (key === 'stl_biz_minutes') {
                aVal = a[key] ?? Infinity;
                bVal = b[key] ?? Infinity;
            } else {
                aVal = String(a[key] ?? '').toLowerCase();
                bVal = String(b[key] ?? '').toLowerCase();
            }
            if (aVal < bVal) return dir === 'asc' ? -1 : 1;
            if (aVal > bVal) return dir === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [mondayReportData.deals, mondayDetailSearch, mondayDetailSort, DEAL_DETAIL_STATIC_KEYS]);

    // Dynamic disposition columns: only show dispositions that have at least one non-zero count
    const visibleDispositionCols = useMemo(() => {
        const nonZeroKeys = new Set();
        mondayDetailDeals.forEach(deal => {
            Object.entries(deal.disposition_counts || {}).forEach(([key, count]) => {
                if (count > 0) nonZeroKeys.add(key);
            });
        });
        const adminOrder = (dispositions || [])
            .filter(d => d.value && nonZeroKeys.has(d.value))
            .map(d => ({ key: d.value, label: d.label }));
        const adminKeys = new Set(adminOrder.map(d => d.key));
        const extras = [...nonZeroKeys]
            .filter(k => !adminKeys.has(k))
            .map(k => ({ key: k, label: k }));
        return [...adminOrder, ...extras];
    }, [mondayDetailDeals, dispositions]);

    // Lead Detail table totals (computed from currently visible/filtered rows)
    const leadDetailTotals = useMemo(() => {
        const totals = { contacted: 0, conversation: 0 };
        mondayDetailDeals.forEach(deal => {
            if (deal.contacted === 'Yes') totals.contacted++;
            if (deal.conversation === 'Yes') totals.conversation++;
            Object.entries(deal.disposition_counts || {}).forEach(([key, count]) => {
                totals[key] = (totals[key] || 0) + count;
            });
        });
        return totals;
    }, [mondayDetailDeals]);

    // Handler for toggling sort on the monday detail table
    const handleMondayDetailSort = (key) => {
        setMondayDetailSort(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderLeadDetailRow = (deal, idx, dispCols) => (
        <Tr key={deal.deal_id || idx}>
            <Td fontWeight="semibold" maxW="200px" isTruncated>
                <Link
                    href={`/crm/deals/${deal.deal_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.600"
                    _hover={{ color: 'blue.800', textDecoration: 'underline' }}
                >
                    {deal.deal_name || 'Unknown'}
                </Link>
            </Td>
            <Td whiteSpace="nowrap">
                {deal.source_medium ? formatSourceLabel(deal.source_medium) : '\u2014'}
            </Td>
            <Td whiteSpace="nowrap">
                <Badge colorScheme={
                    FUNDED_STAGES_ANALYTICS.includes(deal.stage) ? 'green' :
                    deal.stage === 'deal-lost' ? 'red' :
                    deal.stage?.startsWith('loan-') ? 'purple' :
                    'gray'
                } variant="subtle" fontSize="xs">
                    {getStageLabel(deal.stage)}
                </Badge>
            </Td>
            <Td>
                {deal.contacted === 'N/A' ? <Text color="gray.400" fontSize="xs">{'\u2014'}</Text> : (
                    <Badge colorScheme={deal.contacted === 'Yes' ? 'green' : 'red'} variant="subtle" fontSize="xs">
                        {deal.contacted}
                    </Badge>
                )}
            </Td>
            <Td>
                {deal.conversation === 'N/A' ? <Text color="gray.400" fontSize="xs">{'\u2014'}</Text> : (
                    <Badge colorScheme={deal.conversation === 'Yes' ? 'green' : 'red'} variant="subtle" fontSize="xs">
                        {deal.conversation}
                    </Badge>
                )}
            </Td>
            <Td textAlign="center">
                <IconButton
                    icon={<FiMessageSquare />}
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    aria-label="View notes"
                    onClick={() => openNotesModal(deal.deal_id, deal.deal_name)}
                />
            </Td>
            <Td whiteSpace="nowrap">{deal.owner_name || 'Unknown'}</Td>
            <Td whiteSpace="nowrap">
                {deal.created_at
                    ? new Date(deal.created_at).toLocaleString('en-US', {
                        timeZone: 'America/New_York',
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true,
                    })
                    : '\u2014'}
            </Td>
            <Td whiteSpace="nowrap">
                {deal.first_contact
                    ? new Date(deal.first_contact).toLocaleString('en-US', {
                        timeZone: 'America/New_York',
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true,
                    })
                    : '\u2014'}
            </Td>
            <Td isNumeric whiteSpace="nowrap">
                {deal.stl_biz_minutes != null
                    ? <Badge colorScheme={
                        deal.stl_biz_minutes <= 30 ? 'green' :
                        deal.stl_biz_minutes <= 60 ? 'yellow' : 'red'
                    } variant="subtle" fontSize="xs">
                        {Math.round(deal.stl_biz_minutes)} min
                    </Badge>
                    : '\u2014'}
            </Td>
            {dispCols.map(col => (
                <Td key={col.key} isNumeric>
                    {(deal.disposition_counts || {})[col.key] || 0}
                </Td>
            ))}
        </Tr>
    );

    const openNotesModal = async (dealId, dealName) => {
        setNotesModalDealName(dealName || 'Unknown');
        setNotesModalActivities([]);
        setNotesModalLoading(true);
        onNotesModalOpen();

        const { data, error } = await supabase
            .from('crm_activities')
            .select('*')
            .eq('deal_id', dealId)
            .order('created_at', { ascending: false });

        if (!error && data) {
            const transformed = data.map(activity => ({
                id: activity.id,
                text: activity.content,
                authorId: activity.author_id,
                timestamp: activity.created_at,
                activityType: activity.type,
                ...(activity.metadata || {}),
            }));
            setNotesModalActivities(transformed);
        }
        setNotesModalLoading(false);
    };

    const openMondayCardDrilldown = (filterKey) => {
        setMondayCardFilterKey(filterKey);
        setMondayCardSearch('');
        setMondayCardSort({ key: 'created_at', dir: 'desc' });
        onMondayCardModalOpen();
    };

    const handleMondayCardSort = (key) => {
        setMondayCardSort(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
        }));
    };

    const mondayCardDrilldownDeals = useMemo(() => {
        if (!mondayCardFilterKey) return [];
        const config = MONDAY_CARD_FILTERS[mondayCardFilterKey];
        if (!config) return [];

        let rows = config.source === 'periodClosingDeals'
            ? (mondayReportData.periodClosingDeals || [])
            : (mondayReportData.deals || []).filter(config.filterFn);

        if (mondayCardSearch.trim()) {
            const q = mondayCardSearch.trim().toLowerCase();
            rows = rows.filter(d =>
                (d.deal_name || '').toLowerCase().includes(q) ||
                (d.source_medium || '').toLowerCase().includes(q) ||
                (d.owner_name || '').toLowerCase().includes(q) ||
                (d.stage || '').toLowerCase().includes(q)
            );
        }

        const { key, dir } = mondayCardSort;
        const isDispositionKey = !DEAL_DETAIL_STATIC_KEYS.has(key);
        return [...rows].sort((a, b) => {
            let aVal, bVal;
            if (isDispositionKey) {
                aVal = (a.disposition_counts?.[key] || 0);
                bVal = (b.disposition_counts?.[key] || 0);
            } else if (key === 'created_at' || key === 'first_contact') {
                aVal = a[key] ? new Date(a[key]).getTime() : 0;
                bVal = b[key] ? new Date(b[key]).getTime() : 0;
            } else if (key === 'stl_biz_minutes') {
                aVal = a[key] ?? Infinity;
                bVal = b[key] ?? Infinity;
            } else {
                aVal = String(a[key] ?? '').toLowerCase();
                bVal = String(b[key] ?? '').toLowerCase();
            }
            if (aVal < bVal) return dir === 'asc' ? -1 : 1;
            if (aVal > bVal) return dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [mondayCardFilterKey, mondayReportData, mondayCardSearch, mondayCardSort, DEAL_DETAIL_STATIC_KEYS]);

    // --- Init: Fetch Users & Setup for Admin/Non-Admin ---
    useEffect(() => {
        const init = async () => {
            if (!currentUser) return;

            if (isCRMAdmin) {
                // Admins can see all users - fetch the full list
                try {
                    const usersRef = collection(db, 'users');
                    const q = query(usersRef, where('CRMUser', '==', true)); 
                    const snap = await getDocs(q);
                    
                    if (!snap.empty) {
                        const userList = sortCRMUsersWithDTCPriority(
                            snap.docs.map(d => ({
                                label: d.data().Name || d.data().name || d.data().email,
                                value: d.id,
                                isDTCLoanOfficer: d.data().isDTCLoanOfficer || false,
                                Roles: d.data().Roles || [],
                            })),
                            'label'
                        );
                        
                        setUsers(userList);
                        
                        // Reconcile URL users param against loaded user list
                        if (urlUsersRef.current) {
                            const urlUserIds = urlUsersRef.current.split(',').filter(Boolean);
                            const matchedUsers = userList.filter(u => urlUserIds.includes(u.value));
                            if (matchedUsers.length > 0) {
                                setCommittedUsers(matchedUsers);
                            }
                            urlUsersRef.current = null;
                        }
                        // else: no URL param — keep default (no users selected = all)
                    }
                } catch (err) {
                    console.error("Error fetching CRM users:", err);
                }
            } else {
                // Non-admins can only see their own data - lock to current user
                const userOption = {
                    label: currentUser.displayName || currentUser.email || 'You',
                    value: currentUser.uid
                };
                setUsers([userOption]);
                setCommittedUsers([userOption]); // Commit and lock to current user
                urlUsersRef.current = null; // Clear ref — non-admins ignore URL users param
            }
        };
        init();
    }, [currentUser, isCRMAdmin]);

    // --- Load Filter Options ---
    // Stage options now come from useCrmSettings (crm_deal_stages table)
    // Source options still loaded from distinct values in crm_deals
    useEffect(() => {
        // Set stage options from CRM settings (all configured stages)
        if (dealStages && dealStages.length > 0 && !stagesLoading) {
            const stageOpts = dealStages.map(s => ({ 
                value: s.value, 
                label: s.label || s.value 
            }));
            setStageOptions(stageOpts);
            
            // Reconcile URL stages param against loaded stage options.
            // Zero matches commits [] — no filter (All), same as the default.
            if (urlStagesRef.current) {
                const urlStageValues = urlStagesRef.current.split(',').filter(Boolean);
                const matchedStages = stageOpts.filter(s => urlStageValues.includes(s.value));
                setCommittedStages(matchedStages);
                urlStagesRef.current = null;
            }
            // else: no URL param — keep default (nothing selected = all)
        }
    }, [dealStages, stagesLoading]);

    // Load source + tag options using SQL RPCs (each returns distinct values).
    // Both are loaded in ONE effect because setFiltersLoaded gates the first
    // data fetch: loading tags separately would let that fetch fire before the
    // URL tags param had been reconciled, producing one wrong result and a
    // wasted round trip.
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [sourceRes, tagRes] = await Promise.all([
                    supabase.rpc('crm_distinct_source_mediums'),
                    supabase.rpc('crm_distinct_deal_tags'),
                ]);

                if (sourceRes.error) {
                    console.error('Error fetching sources via RPC:', sourceRes.error);
                }
                if (tagRes.error) {
                    console.error('Error fetching deal tags via RPC:', tagRes.error);
                }

                const sourceOpts = (sourceRes.data || [])
                    .map(row => row.source_medium)
                    .filter(Boolean)
                    .map(s => ({ 
                        value: s, 
                        label: formatSourceLabel(s) 
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                
                setSourceOptions(sourceOpts);
                
                // Reconcile URL sources param against loaded source options.
                // Zero matches commits [] — no filter (All), same as the default.
                if (urlSourcesRef.current) {
                    const urlSourceValues = urlSourcesRef.current.split(',').filter(Boolean);
                    const matchedSources = sourceOpts.filter(s => urlSourceValues.includes(s.value));
                    setCommittedSourceMediums(matchedSources);
                    urlSourcesRef.current = null;
                }
                // else: no URL param — keep default (nothing selected = all)

                // Tag options: only tags actually applied to deals, with the
                // "(No tags)" pseudo-option pinned first so it is easy to find.
                const tagOpts = [
                    { value: NO_TAGS_VALUE, label: '(No tags)' },
                    ...(tagRes.data || [])
                        .filter(row => row.tag_value)
                        .map(row => ({
                            value: row.tag_value,
                            label: row.tag_label || row.tag_value,
                        })),
                ];

                setTagOptions(tagOpts);

                // Reconcile URL tags param against loaded tag options.
                // Zero matches commits [] — no filter (All), same as the default.
                if (urlTagsRef.current) {
                    const urlTagValues = urlTagsRef.current.split(',').filter(Boolean);
                    const matchedTags = tagOpts.filter(t => urlTagValues.includes(t.value));
                    setCommittedTags(matchedTags);
                    urlTagsRef.current = null;
                }
                // else: no URL param — keep default (nothing selected = all)

                setFiltersLoaded(true);
            } catch (err) {
                console.error('Error loading filter options:', err);
                setFiltersLoaded(true); // Continue even if load fails
            }
        };
        
        loadFilterOptions();
    }, []);

    // --- Lazy Tab Loading ---
    // Maps each tab to the batch report(s) it needs. Tabs 1 & 2 share batch_activity.
    const TAB_TO_BATCHES = useMemo(() => ({
        0: ['batch_overview', 'monday_report'],
        1: ['batch_activity'],
        2: ['batch_activity'],
        3: ['batch_health'],
        4: ['monday_report'],
        5: ['batch_campaigns']
    }), []);

    // Track which batches have been loaded for the current filter set
    const loadedBatchesRef = useRef(new Set());
    const filterHashRef = useRef('');

    // Helpers to process each batch response into state
    const processOverview = (overviewData) => {
        setSummary(sanitizeSummaryObject(overviewData.summary?.[0], SUMMARY_SCHEMA));
        setVolumeData(sanitizeChartData(overviewData.volume || [], 'source', ['total_leads', 'funded_count']));
        setFunnelData(sanitizeChartData(overviewData.funnel || [], 'stage', ['count', 'total_volume']));
        setLoStats(sanitizeNumericData(
            overviewData.loPerformance || [],
            ['total_leads', 'funded', 'lost', 'submissions', 'pipeline_value', 'conversion_rate'],
            ['lo_name', 'ownerId']
        ));
    };

    const processActivity = (activityData) => {
        setUserActivityData(sanitizeNumericData(
            activityData.userActivity || [], 
            ['deals_touched', 'avg_deals_per_day', 'calls_left_message', 'calls_no_answer', 
             'calls_spoke', 'calls_wrong_number', 'total_call_dispositions', 'emails_sent',
             'quotes_sent', 'meetings_scheduled', 'active_days', 'avg_calls_per_day', 'total_activities'],
            ['user_name', 'user_id']
        ));
        setTaskAnalyticsData(sanitizeNumericData(
            activityData.taskAnalytics || [],
            ['total_active_deals', 'total_tasks', 'avg_tasks_per_deal', 'deals_without_tasks',
             'deals_with_overdue_tasks', 'total_overdue_tasks', 'pct_deals_no_tasks', 'pct_deals_overdue'],
            ['user_name', 'user_id']
        ));
        setTaskHealthSummary(sanitizeSummaryObject(activityData.taskHealth?.[0], TASK_HEALTH_SCHEMA));
        setActivitySummary(sanitizeSummaryObject(activityData.activitySummary?.[0], ACTIVITY_SUMMARY_SCHEMA));
    };

    const processHealth = (healthData) => {
        setStagnantLeads(sanitizeNumericData(
            healthData.stagnantLeads || [],
            ['days_stagnant', 'threshold_days'],
            ['deal_id', 'dealName', 'stage', 'ownerId', 'owner_name']
        ));
        setLeadsWithoutFollowup(sanitizeNumericData(
            healthData.leadsWithoutFollowup || [],
            [],
            ['deal_id', 'dealName', 'stage', 'ownerId', 'owner_name']
        ));
        setSpeedToLeadData(sanitizeNumericData(
            healthData.speedToLead || [],
            ['avg_response_minutes', 'avg_response_hours', 'total_leads', 'leads_with_response', 'leads_no_response'],
            ['user_name', 'user_id']
        ));
        setAppointmentsAppsData(sanitizeNumericData(
            healthData.appointmentsApps || [],
            ['appointments_set', 'apps_taken'],
            ['user_name', 'user_id']
        ));
    };

    const processMonday = (mondayData) => {
        setMondayReportData({
            byUser: sanitizeNumericData(
                mondayData.byUser || [],
                ['total_leads', 'avg_stl', 'avg_stl_biz', 'active_leads', 'leads_with_conv', 
                 'conv_pct', 'leads_contacted', 'contacted_pct', 'leads_lost_cohort', 
                 'lost_cohort_pct', 'lost_period', 'closings_period', 'in_app', 
                 'pre_approved', 'in_processing'],
                ['user_name', 'user_id']
            ),
            totals: sanitizeSummaryObject(mondayData.totals, MONDAY_REPORT_TOTALS_SCHEMA),
            deals: mondayData.deals || [],
            periodClosingDeals: mondayData.periodClosingDeals || []
        });
    };

    const processCampaigns = (campaignData) => {
        setCampaignAnalytics({
            summary: sanitizeSummaryObject(campaignData.summary, CAMPAIGN_SUMMARY_SCHEMA),
            campaigns: sanitizeNumericData(
                campaignData.campaigns || [],
                ['steps', 'total_subscribers', 'active_subscribers', 'enrolled', 'active_now',
                 'completed', 'failed', 'cancelled', 'steps_executed', 'success_rate',
                 'emails_sent', 'unique_opens', 'open_rate', 'unique_clicks', 'click_rate',
                 'sms_sent', 'sms_failed', 'funded_deals', 'funded_volume'],
                ['template_id', 'name', 'category', 'trigger_type']
            )
        });
    };

    // --- Data Fetching (lazy per-tab) ---
    useEffect(() => {
        const fetchData = async () => {
            if (!filtersLoaded) return;
            if (dateRangeCode === 'custom' && (!customStartDate || !customEndDate)) return;
            
            // Compute a hash of the current filter state to detect changes
            const currentHash = JSON.stringify({
                dateRangeCode, customStartDate, customEndDate,
                users: committedUsers.map(u => u.value),
                stages: committedStages.map(s => s.value),
                sources: committedSourceMediums.map(s => s.value),
                tags: committedTags.map(t => t.value),
                stageLen: stageOptions.length, sourceLen: sourceOptions.length,
                tagLen: tagOptions.length,
                refreshKey
            });

            // If filters changed, reset loaded-batch tracker
            if (currentHash !== filterHashRef.current) {
                filterHashRef.current = currentHash;
                loadedBatchesRef.current = new Set();
                // Evict expired cache entries to prevent memory growth
                const now = Date.now();
                for (const [key, entry] of responseCacheRef.current) {
                    if (now - entry.timestamp > CACHE_TTL_MS) {
                        responseCacheRef.current.delete(key);
                    }
                }
            }

            // Determine which batches the current tab needs
            const neededBatches = (TAB_TO_BATCHES[tabIndex] || ['batch_overview'])
                .filter(b => !loadedBatchesRef.current.has(b));

            if (neededBatches.length === 0) return;

            setLoading(true);
            setError(null);
            
            try {
                const queryFn = httpsCallable(functions, 'queryCrmAnalyticsV2');
                const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
                const userIds = committedUsers ? committedUsers.map(u => u.value) : [];
                const stages = committedStages.length === stageOptions.length 
                    ? [] : committedStages.map(s => s.value);
                const sourceMediums = committedSourceMediums.length === sourceOptions.length 
                    ? [] : committedSourceMediums.map(s => s.value);
                const tags = buildTagsPayload(committedTags, tagOptions.length);
                const commonPayload = { 
                    dateRange: { start, end },
                    filters: { userIds, stages, sourceMediums, tags }
                };

                const totalStartTime = performance.now();
                
                // Check TTL cache for each batch; only fetch batches that aren't cached
                const now = Date.now();
                const cachedResults = [];
                const uncachedBatches = [];
                
                neededBatches.forEach(batch => {
                    const cacheKey = `${currentHash}::${batch}`;
                    const cached = responseCacheRef.current.get(cacheKey);
                    if (cached && (now - cached.timestamp) < CACHE_TTL_MS) {
                        cachedResults.push({ batch, data: cached.data });
                        loadedBatchesRef.current.add(batch);
                    } else {
                        uncachedBatches.push(batch);
                    }
                });

                // Apply cached results immediately
                cachedResults.forEach(({ batch, data }) => {
                    switch (batch) {
                        case 'batch_overview': processOverview(data || {}); break;
                        case 'batch_activity': processActivity(data || {}); break;
                        case 'batch_health':   processHealth(data || {}); break;
                        case 'monday_report':  processMonday(data || { byUser: [], totals: {} }); break;
                        case 'batch_campaigns': processCampaigns(data || {}); break;
                    }
                });

                if (cachedResults.length > 0) {
                    console.log(`%c📦 Cache hit: ${cachedResults.map(r => r.batch).join(', ')}`, 'color: #38A169;');
                }
                
                if (uncachedBatches.length === 0) {
                    setLoading(false);
                    return;
                }

                console.log(`%c📊 CRM Analytics - Fetching: ${uncachedBatches.join(', ')} (tab ${tabIndex})`, 'color: #3182CE; font-weight: bold;');

                // Fetch only uncached batches (in parallel)
                const batchPromises = uncachedBatches.map(batch =>
                    queryFn({ ...commonPayload, reportType: batch }).then(res => ({ batch, data: res.data.data }))
                );
                const results = await Promise.all(batchPromises);
                
                // Store fetched results in cache
                results.forEach(({ batch, data }) => {
                    const cacheKey = `${currentHash}::${batch}`;
                    responseCacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
                });

                const apiEndTime = performance.now();
                console.log(`%c✅ API calls complete: ${(apiEndTime - totalStartTime).toFixed(0)}ms`, 'color: #38A169; font-weight: bold;');

                // Process each batch result into state
                results.forEach(({ batch, data }) => {
                    loadedBatchesRef.current.add(batch);
                    switch (batch) {
                        case 'batch_overview': processOverview(data || {}); break;
                        case 'batch_activity': processActivity(data || {}); break;
                        case 'batch_health':   processHealth(data || {}); break;
                        case 'monday_report':  processMonday(data || { byUser: [], totals: {} }); break;
                        case 'batch_campaigns': processCampaigns(data || {}); break;
                    }
                });

                // After loading the active tab, prefetch remaining batches in background
                const allBatches = ['batch_overview', 'batch_activity', 'batch_health', 'monday_report', 'batch_campaigns'];
                const remainingBatches = allBatches.filter(b => !loadedBatchesRef.current.has(b));
                if (remainingBatches.length > 0) {
                    const savedHash = filterHashRef.current;
                    Promise.all(
                        remainingBatches.map(batch =>
                            queryFn({ ...commonPayload, reportType: batch }).then(res => ({ batch, data: res.data.data }))
                        )
                    ).then(bgResults => {
                        if (filterHashRef.current !== savedHash) return;
                        bgResults.forEach(({ batch, data }) => {
                            loadedBatchesRef.current.add(batch);
                            // Store in cache
                            const cacheKey = `${currentHash}::${batch}`;
                            responseCacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
                            switch (batch) {
                                case 'batch_overview': processOverview(data || {}); break;
                                case 'batch_activity': processActivity(data || {}); break;
                                case 'batch_health':   processHealth(data || {}); break;
                                case 'monday_report':  processMonday(data || { byUser: [], totals: {} }); break;
                                case 'batch_campaigns': processCampaigns(data || {}); break;
                            }
                        });
                        console.log(`%c🔄 Background prefetch complete: ${remainingBatches.join(', ')}`, 'color: #718096;');
                    }).catch(err => console.warn('Background prefetch error:', err));
                }

                const totalEndTime = performance.now();
                console.log(`%c🏁 Active tab load: ${(totalEndTime - totalStartTime).toFixed(0)}ms`, 'color: #805AD5; font-weight: bold;');

            } catch (err) {
                console.error("Analytics Error:", err);
                setError(err.message || 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRangeCode, committedUsers, customStartDate, customEndDate, committedStages, committedSourceMediums, committedTags, filtersLoaded, stageOptions.length, sourceOptions.length, tagOptions.length, refreshKey, tabIndex, TAB_TO_BATCHES]);
    
    // --- Sync committed filter state to URL ---
    // Uses history.replace() so filter changes don't stack in browser history.
    // Back button will go to the previous PAGE, not the previous filter state.
    useEffect(() => {
        const params = new URLSearchParams();

        // Date range (omit default 'last_week' to keep URL clean)
        if (dateRangeCode && dateRangeCode !== 'last_week') {
            params.set('range', dateRangeCode);
        }
        if (dateRangeCode === 'custom') {
            if (customStartDate) params.set('start', customStartDate);
            if (customEndDate) params.set('end', customEndDate);
        }

        // Users (only relevant for admins; non-admins are locked to self)
        if (isCRMAdmin && committedUsers.length > 0) {
            params.set('users', committedUsers.map(u => u.value).join(','));
        }

        // Stages (only if not "all" — omitting means all selected)
        if (committedStages.length > 0 && committedStages.length < stageOptions.length) {
            params.set('stages', committedStages.map(s => s.value).join(','));
        }

        // Sources (only if not "all" — omitting means all selected)
        if (committedSourceMediums.length > 0 && committedSourceMediums.length < sourceOptions.length) {
            params.set('sources', committedSourceMediums.map(s => s.value).join(','));
        }

        // Tags (only if not "all" — omitting means all selected).
        // URLSearchParams percent-encodes values, so tags containing spaces,
        // commas, apostrophes or emoji round-trip safely.
        if (committedTags.length > 0 && committedTags.length < tagOptions.length) {
            params.set('tags', committedTags.map(t => t.value).join(','));
        }

        // Tab (omit default 0 to keep URL clean)
        if (tabIndex > 0) {
            params.set('tab', String(tabIndex));
        }

        const search = params.toString();
        const newSearch = search ? `?${search}` : '';
        
        // Only replace if the search string actually changed (prevents infinite loops)
        if (location.search !== newSearch) {
            history.replace({ pathname: location.pathname, search: newSearch });
        }
    }, [dateRangeCode, customStartDate, customEndDate, committedUsers, committedStages, committedSourceMediums, committedTags, tabIndex, stageOptions.length, sourceOptions.length, tagOptions.length, isCRMAdmin, history, location.search, location.pathname]);

    // --- FutureBot agent registration ---
    // One state+setter snapshot per render; the hook reads it through a ref so
    // its stable tool closures always see live data. Must stay above the
    // component's return (hooks rule).
    const { registration: agentRegistration } = useAnalyticsAgentTools({
        isCRMAdmin,
        currentUser,
        loading,
        error,
        dateRangeCode,
        customStartDate,
        customEndDate,
        tabIndex,
        users,
        stageOptions,
        sourceOptions,
        tagOptions,
        committedUsers,
        committedStages,
        committedSourceMediums,
        committedTags,
        setDateRangeCode,
        setCustomStartDate,
        setCustomEndDate,
        setCommittedUsers,
        setCommittedStages,
        setCommittedSourceMediums,
        setCommittedTags,
        setTabIndex,
        getDateRange: () => getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate),
        computeDateRange: getDateRangeFromCode,
        loadedBatches: [...loadedBatchesRef.current],
        summary,
        funnelData,
        volumeData,
        loStats,
        userActivityData,
        taskAnalyticsData,
        taskHealthSummary,
        activitySummary,
        stagnantLeads,
        leadsWithoutFollowup,
        speedToLeadData,
        appointmentsAppsData,
        mondayReportData,
        campaignAnalytics,
    });
    useAgentPage(agentRegistration);

    // FeatureTour: named actions the analytics tours can invoke — click the
    // tagged tab header (never index math) so isLazy panels mount naturally.
    useTourActions(useMemo(() => {
        const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
        return {
            showOverviewTab: () => clickTab('analytics-tab-overview'),
            showTaskHealthTab: () => clickTab('analytics-tab-task'),
            showLeadHealthTab: () => clickTab('analytics-tab-lead'),
            showMondayTab: () => clickTab('analytics-tab-monday'),
            showCampaignsTab: () => clickTab('analytics-tab-campaigns'),
        };
    }, []));

    // --- Drill-Down Functions ---
    const fetchDrillDown = async (reportType, title) => {
        setDrillDownLoading(true);
        setDrillDownTitle(title);
        setDrillDownData([]);
        
        try {
            const drillStartTime = performance.now();
            console.log(`%c🔍 Drill-down: ${reportType}`, 'color: #DD6B20; font-weight: bold;');
            
            const queryFn = httpsCallable(functions, 'queryCrmAnalyticsV2');
            const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
            // Use COMMITTED filter values for drill-down to match the displayed data
            const userIds = committedUsers ? committedUsers.map(u => u.value) : [];
            
            // Include stage/source/tag filters (use committed values)
            const stages = committedStages.length === stageOptions.length 
                ? [] 
                : committedStages.map(s => s.value);
            const sourceMediums = committedSourceMediums.length === sourceOptions.length 
                ? [] 
                : committedSourceMediums.map(s => s.value);
            const tags = buildTagsPayload(committedTags, tagOptions.length);
            
            const res = await queryFn({
                dateRange: { start, end },
                filters: { userIds, stages, sourceMediums, tags },
                reportType
            });
            
            console.log(`   ⏱️ ${reportType}: ${(performance.now() - drillStartTime).toFixed(0)}ms (${res.data.data?.length || 0} rows)`);
            
            // Sanitize drill-down data to ensure no undefined values cause crashes
            const rawData = res.data.data || [];
            const sanitizedData = rawData.map(item => {
                if (!item || typeof item !== 'object') return {};
                // Ensure all numeric fields that might be displayed are safe
                return {
                    ...item,
                    days_stagnant: safeNumber(item.days_stagnant),
                    days_overdue: safeNumber(item.days_overdue),
                    overdue_task_count: safeNumber(item.overdue_task_count)
                };
            });
            setDrillDownData(sanitizedData);
        } catch (err) {
            console.error("Drill-down fetch error:", err);
            setDrillDownData([]);
        } finally {
            setDrillDownLoading(false);
        }
    };
    
    const handleOverdueTasksClick = () => {
        fetchDrillDown('drill_down_overdue_tasks', 'Overdue Tasks');
        onOverdueModalOpen();
    };
    
    const handleDealsWithOverdueClick = () => {
        fetchDrillDown('drill_down_deals_with_overdue', 'Deals With Overdue Tasks');
        onDealsWithOverdueModalOpen();
    };
    
    const handleStagnantClick = () => {
        fetchDrillDown('drill_down_stagnant', 'Stagnant Leads');
        onStagnantModalOpen();
    };
    
    const handleNoFollowupClick = () => {
        fetchDrillDown('drill_down_no_followup', 'Leads Without Follow-up');
        onNoFollowupModalOpen();
    };
    
    const handleDealsWithoutTasksClick = () => {
        fetchDrillDown('drill_down_deals_without_tasks', 'Deals Without Tasks');
        onDealsWithoutTasksModalOpen();
    };

    // --- Campaigns Tab: derived table rows + drill-down handlers ---
    const visibleCampaignRows = useMemo(() => {
        let rows = campaignAnalytics.campaigns || [];
        if (campaignSearch.trim()) {
            const q = campaignSearch.trim().toLowerCase();
            rows = rows.filter(c =>
                (c.name || '').toLowerCase().includes(q) ||
                (c.category || '').toLowerCase().includes(q) ||
                (c.trigger_type || '').toLowerCase().includes(q)
            );
        }
        const { key, dir } = campaignSort;
        return [...rows].sort((a, b) => {
            let aVal, bVal;
            if (key === 'name' || key === 'category' || key === 'trigger_type') {
                aVal = String(a[key] ?? '').toLowerCase();
                bVal = String(b[key] ?? '').toLowerCase();
            } else if (key === 'last_activity_at') {
                aVal = a[key] ? new Date(a[key]).getTime() : 0;
                bVal = b[key] ? new Date(b[key]).getTime() : 0;
            } else {
                aVal = Number(a[key]) || 0;
                bVal = Number(b[key]) || 0;
            }
            if (aVal < bVal) return dir === 'asc' ? -1 : 1;
            if (aVal > bVal) return dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [campaignAnalytics.campaigns, campaignSearch, campaignSort]);

    // Top 10 campaigns by enrollments for the tab's bar chart
    const topCampaignChartData = useMemo(() => (
        sanitizeChartData(
            (campaignAnalytics.campaigns || [])
                .filter(c => (Number(c.enrolled) || 0) > 0)
                .slice()
                .sort((a, b) => (Number(b.enrolled) || 0) - (Number(a.enrolled) || 0))
                .slice(0, 10)
                .map(c => ({
                    name: (c.name || 'Untitled').length > 24 ? `${c.name.slice(0, 24)}…` : (c.name || 'Untitled'),
                    enrolled: c.enrolled,
                    completed: c.completed,
                })),
            'name',
            ['enrolled', 'completed']
        )
    ), [campaignAnalytics.campaigns]);

    const handleCampaignSort = (key) => {
        setCampaignSort(prev => ({
            key,
            dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
        }));
    };

    const openCampaignDetail = async (campaign) => {
        setCampaignDetailTarget({ templateId: campaign.template_id, name: campaign.name });
        setCampaignDetail(null);
        setCampaignDetailLoading(true);
        onCampaignDetailOpen();

        try {
            console.log(`%c🔍 Campaign detail: ${campaign.name}`, 'color: #DD6B20; font-weight: bold;');
            const queryFn = httpsCallable(functions, 'queryCrmAnalyticsV2');
            const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
            const userIds = committedUsers ? committedUsers.map(u => u.value) : [];
            const res = await queryFn({
                dateRange: { start, end },
                filters: { userIds, stages: [], sourceMediums: [], tags: [] },
                reportType: 'campaign_detail',
                templateId: campaign.template_id,
            });
            setCampaignDetail(res.data.data || null);
        } catch (err) {
            console.error('Campaign detail fetch error:', err);
            toast({
                title: 'Failed to load campaign details',
                description: err.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setCampaignDetailLoading(false);
        }
    };

    const openCampaignLogs = (campaign) => {
        setCampaignLogsTarget({ templateId: campaign.template_id, name: campaign.name });
        onCampaignLogsOpen();
    };

    return (
        <Box p={6} bg="gray.50" minH="100vh" ref={dashboardRef}>
            {/* Header & Controls */}
            <Flex justify="space-between" align="start" mb={6} direction={{base:'column', md:'row'}} gap={4}>
                <Box data-tour="analytics-header">
                    <Heading size="lg" color="blue.800">Team Analytics</Heading>
                    <Text fontSize="sm" color="gray.600">Comprehensive CRM performance insights and activity metrics</Text>
                </Box>
                
                <Flex gap={3} w={{base: '100%', md: 'auto'}} direction={{base: 'column', md: 'row'}} align="center" flexWrap="wrap" data-tour="analytics-filters">
                    {/* User Filter - Only show for admins */}
                    {isCRMAdmin && (
                        <MultiSelectFilterDropdown
                            label="Users"
                            icon={FiUsers}
                            colorScheme="blue"
                            options={users}
                            committedValues={committedUsers}
                            onApply={setCommittedUsers}
                            isLoading={false}
                        />
                    )}
                    
                    {/* Stage Filter */}
                    <MultiSelectFilterDropdown
                        label="Stages"
                        icon={FiLayers}
                        colorScheme="teal"
                        options={stageOptions}
                        committedValues={committedStages}
                        onApply={setCommittedStages}
                        isLoading={stagesLoading}
                    />
                    
                    {/* Source Filter */}
                    <MultiSelectFilterDropdown
                        label="Sources"
                        icon={FiFilter}
                        colorScheme="purple"
                        options={sourceOptions}
                        committedValues={committedSourceMediums}
                        onApply={setCommittedSourceMediums}
                        isLoading={!filtersLoaded}
                    />

                    {/* Tag Filter */}
                    <MultiSelectFilterDropdown
                        label="Tags"
                        icon={FiTag}
                        colorScheme="pink"
                        options={tagOptions}
                        committedValues={committedTags}
                        onApply={setCommittedTags}
                        isLoading={!filtersLoaded}
                    />

                    <ChakraSelect w="180px" bg="white" value={dateRangeCode} onChange={e => setDateRangeCode(e.target.value)} data-tour="analytics-daterange">
                        {DATE_RANGE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </ChakraSelect>
                    
                    {/* Custom Date Range Inputs */}
                    {dateRangeCode === 'custom' && (
                        <HStack spacing={2} bg="white" p={2} borderRadius="md" borderWidth="1px">
                            <FormControl size="sm">
                                <FormLabel fontSize="xs" mb={0}>Start</FormLabel>
                                <Input 
                                    type="date" 
                                    size="sm" 
                                    value={customStartDate}
                                    onChange={e => setCustomStartDate(e.target.value)}
                                />
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel fontSize="xs" mb={0}>End</FormLabel>
                                <Input 
                                    type="date" 
                                    size="sm"
                                    value={customEndDate}
                                    onChange={e => setCustomEndDate(e.target.value)}
                                />
                            </FormControl>
                        </HStack>
                    )}

                    <Button 
                        leftIcon={<FiRefreshCw />} 
                        onClick={() => setRefreshKey(k => k + 1)} 
                        colorScheme="green" 
                        size="md"
                        isLoading={loading}
                        loadingText="Refreshing"
                        data-tour="analytics-refresh"
                    >
                        Refresh
                    </Button>
                </Flex>
            </Flex>

            {error && <Alert status="error" mb={6}><AlertIcon />{error}</Alert>}

            {/* Tabs for organized sections */}
            <Tabs colorScheme="blue" variant="enclosed" bg="white" borderRadius="lg" shadow="sm" mb={6} index={tabIndex} onChange={setTabIndex} isLazy>
                <TabList flexWrap="wrap" data-tour="analytics-tabs">
                    <Tab fontWeight="semibold" data-tour="analytics-tab-overview">Overview</Tab>
                    <Tab fontWeight="semibold" data-tour="analytics-tab-activity">User Activity</Tab>
                    <Tab fontWeight="semibold" data-tour="analytics-tab-task">Task Health</Tab>
                    <Tab fontWeight="semibold" data-tour="analytics-tab-lead">Lead Health</Tab>
                    <Tab fontWeight="semibold" data-tour="analytics-tab-monday">Monday Report</Tab>
                    <Tab fontWeight="semibold" data-tour="analytics-tab-campaigns">Campaigns</Tab>
                </TabList>

                <TabPanels>
                    {/* TAB 1: Overview (existing content) */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Overview Error" message="Unable to load the Overview tab. Please try refreshing.">
                        {/* KPI Grid - Row 1: Volume */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={5} data-tour="analytics-kpis">
                            <KpiCard label="Total Leads" value={summary.total_leads || 0} subtext="in date range" loading={loading} />
                            <KpiCard label="Active Leads" value={summary.active_leads || 0} subtext="Prospecting / New" loading={loading} />
                            <KpiCard label="Active Loans" value={summary.active_pipeline || 0} subtext="Intake to Processing" loading={loading} />
                            <KpiCard label="Won Deals" value={summary.won_count || 0} subtext={`${summary.lost_count || 0} lost`} loading={loading} />
                        </SimpleGrid>

                        {/* KPI Grid - Row 2: Performance */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={8}>
                            <KpiCard 
                                label="Conversion Rate" 
                                value={`${(summary.conversion_rate || 0)}%`} 
                                subtext="team average"
                                loading={loading} 
                            />
                            <KpiCard 
                                label="Avg Deal Value" 
                                value={`$${Math.round(summary.avg_deal_value || 0).toLocaleString()}`} 
                                subtext="per won deal"
                                loading={loading} 
                            />
                            <KpiCard 
                                label="Avg Close Time" 
                                value={summary.avg_close_days ? `${Math.round(summary.avg_close_days)} Days` : 'N/A'} 
                                subtext="from lead to won"
                                loading={loading} 
                            />
                            <KpiCard 
                                label="Avg Response Time" 
                                value={summary.avg_response_minutes ? `${(summary.avg_response_minutes / 60).toFixed(1)}h` : 'N/A'} 
                                subtext="from lead to first contact"
                                loading={loading} 
                            />
                        </SimpleGrid>

                        {/* Main Content Grid */}
                        <Grid templateColumns="repeat(12, 1fr)" gap={6}>
                            {/* Pipeline Funnel - Dynamic from CRM settings */}
                            <GridItem colSpan={{ base: 12, lg: 6 }} bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" data-tour="analytics-funnel">
                                <Heading size="md" mb={6}>Conversion Funnel</Heading>
                                {(loading || stagesLoading) ? <Skeleton height="300px" /> : (
                                    <Box>
                                        {dealStages
                                            .filter(stageConfig => {
                                                // Only show stages that have deals (count > 0)
                                                const stageData = funnelData.find(d => d.stage === stageConfig.value);
                                                return stageData && parseInt(stageData.count) > 0;
                                            })
                                            .map(stageConfig => {
                                                const stageData = funnelData.find(d => d.stage === stageConfig.value) || { count: 0 };
                                                const maxVal = Math.max(...funnelData.map(d => parseInt(d.count) || 0), 1); 
                                                const count = parseInt(stageData.count) || 0;
                                                const pct = maxVal > 0 ? (count / maxVal) * 100 : 0;
                                                
                                                return (
                                                    <Box key={stageConfig.value} mb={4}>
                                                        <Flex justify="space-between" mb={1}>
                                                            <Text fontWeight="bold" fontSize="sm">
                                                                {stageConfig.label || stageConfig.value}
                                                            </Text>
                                                            <Text fontSize="sm" fontWeight="bold">{count}</Text>
                                                        </Flex>
                                                        <Progress value={pct} size="sm" colorScheme="blue" borderRadius="full" />
                                                    </Box>
                                                )
                                            })}
                                        {/* Show message if no stages have deals */}
                                        {dealStages.filter(s => {
                                            const d = funnelData.find(f => f.stage === s.value);
                                            return d && parseInt(d.count) > 0;
                                        }).length === 0 && (
                                            <Text color="gray.500" textAlign="center" py={4}>
                                                No deal data available for this selection
                                            </Text>
                                        )}
                                    </Box>
                                )}
                            </GridItem>

                            {/* Leads by Source Chart - Wrapped in Error Boundary */}
                            <GridItem colSpan={{ base: 12, lg: 6 }} h="450px" p={5} borderWidth="1px" borderRadius="lg" bg="gray.50" data-tour="analytics-sources">
                              <Heading size="md" mb={4}>Lead Source Performance</Heading>
                              <AnalyticsErrorBoundary 
                                title="Chart Error" 
                                message="Unable to render the chart. Please try refreshing the page."
                              >
                                {(loading || !chartReady) ? (
                                   <Skeleton height="300px" />
                                ) : (safeVolumeData.length === 0) ? (
                                  <Flex h="300px" align="center" justify="center">
                                    <Text color="gray.500">No lead source data available for this selection</Text>
                                  </Flex>
                                ) : (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart 
                                      data={safeVolumeData} 
                                      layout="vertical" 
                                      margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                                    >
                                      <XAxis 
                                        type="number" 
                                        allowDecimals={false}
                                      />
                                      <YAxis dataKey="source" type="category" width={100} />
                                      <RechartsTooltip />
                                      <Legend />
                                      <Bar dataKey="total_leads" fill="#3182CE" name="Total Leads" radius={[0, 4, 4, 0]} />
                                      <Bar dataKey="funded_count" fill="#38A169" name="Won" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}
                              </AnalyticsErrorBoundary>
                            </GridItem>

                            {/* Lead Detail Table (mirrored from the Monday Report tab) */}
                            <GridItem colSpan={12} bg="gray.50" p={6} borderRadius="lg" borderWidth="1px">
                                <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={3}>
                                    <HStack spacing={3}>
                                        <Heading size="md">Lead Detail</Heading>
                                        <Badge colorScheme="blue" fontSize="sm" px={2} py={0.5} borderRadius="full">
                                            {mondayDetailDeals.length} lead{mondayDetailDeals.length !== 1 ? 's' : ''}
                                        </Badge>
                                    </HStack>
                                    <InputGroup maxW="280px" size="sm">
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={FiSearch} color="gray.400" />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search by name, source, owner..."
                                            value={mondayDetailSearch}
                                            onChange={(e) => setMondayDetailSearch(e.target.value)}
                                            bg="white"
                                            borderRadius="md"
                                        />
                                    </InputGroup>
                                </Flex>
                                <Text fontSize="sm" color="gray.500" mb={4}>
                                    Every lead/deal created in the selected date range and filters. Click a column header to sort.
                                </Text>
                                {loading ? <Skeleton height="300px" /> : (
                                    <TableContainer>
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    {LEAD_DETAIL_COLUMNS.map(col => (
                                                        <Th
                                                            key={col.key}
                                                            cursor="pointer"
                                                            onClick={() => handleMondayDetailSort(col.key)}
                                                            _hover={{ color: 'blue.600' }}
                                                            whiteSpace="nowrap"
                                                        >
                                                            {col.label}
                                                            {mondayDetailSort.key === col.key
                                                                ? (mondayDetailSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                                : ''}
                                                        </Th>
                                                    ))}
                                                    {visibleDispositionCols.map(col => (
                                                        <Th
                                                            key={col.key}
                                                            cursor="pointer"
                                                            onClick={() => handleMondayDetailSort(col.key)}
                                                            _hover={{ color: 'blue.600' }}
                                                            whiteSpace="nowrap"
                                                            isNumeric
                                                        >
                                                            {col.label}
                                                            {mondayDetailSort.key === col.key
                                                                ? (mondayDetailSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                                : ''}
                                                        </Th>
                                                    ))}
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {mondayDetailDeals.map((deal, idx) => renderLeadDetailRow(deal, idx, visibleDispositionCols))}
                                                {mondayDetailDeals.length === 0 && (
                                                    <Tr>
                                                        <Td colSpan={LEAD_DETAIL_COLUMNS.length + visibleDispositionCols.length} textAlign="center" py={8}>
                                                            {mondayDetailSearch.trim()
                                                                ? 'No leads match your search'
                                                                : 'No lead data available for the selected filters and date range'}
                                                        </Td>
                                                    </Tr>
                                                )}
                                            </Tbody>
                                            {mondayDetailDeals.length > 0 && (
                                                <Tfoot>
                                                    <Tr bg="gray.100" borderTopWidth="2px" borderTopColor="gray.300">
                                                        <Td fontWeight="bold">Totals ({mondayDetailDeals.length})</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td fontWeight="bold">{leadDetailTotals.contacted} / {mondayDetailDeals.length}</Td>
                                                        <Td fontWeight="bold">{leadDetailTotals.conversation} / {mondayDetailDeals.length}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        <Td>{'\u2014'}</Td>
                                                        {visibleDispositionCols.map(col => (
                                                            <Td key={col.key} isNumeric fontWeight="bold">
                                                                {leadDetailTotals[col.key] || 0}
                                                            </Td>
                                                        ))}
                                                    </Tr>
                                                </Tfoot>
                                            )}
                                        </Table>
                                    </TableContainer>
                                )}
                            </GridItem>

                            {/* LO Performance Table */}
                            <GridItem colSpan={12} bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" data-tour="analytics-lo-perf">
                                <Heading size="md" mb={6}>Loan Officer Performance</Heading>
                                {loading ? <Skeleton height="200px" /> : (
                                    <TableContainer>
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th>Name</Th>
                                                    <Th isNumeric>Total Leads</Th>
                                                    <Th isNumeric>Active</Th>
                                                    <Th isNumeric>Won</Th>
                                                    <Th isNumeric>Lost</Th>
                                                    <Th isNumeric>Conv. Rate</Th>
                                                    <Th isNumeric>Pipeline Value</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {loStats.map((stat, index) => (
                                                    <Tr key={index}>
                                                        <Td fontWeight="bold">{stat.lo_name || 'Unknown'}</Td>
                                                        <Td isNumeric>{safeNumber(stat.total_leads)}</Td>
                                                        <Td isNumeric>{safeNumber(stat.total_leads) - safeNumber(stat.funded) - safeNumber(stat.submissions) - safeNumber(stat.lost)}</Td> 
                                                        <Td isNumeric>{safeNumber(stat.funded)}</Td>
                                                        <Td isNumeric>{safeNumber(stat.lost)}</Td>
                                                        <Td isNumeric>
                                                            <Badge colorScheme={safeNumber(stat.conversion_rate) > 10 ? 'green' : 'yellow'}>
                                                                {safeNumber(stat.conversion_rate)}%
                                                            </Badge>
                                                        </Td>
                                                        <Td isNumeric>${safeNumber(stat.pipeline_value).toLocaleString()}</Td>
                                                    </Tr>
                                                ))}
                                                {loStats.length === 0 && (
                                                    <Tr><Td colSpan={7} textAlign="center">No data available for this period</Td></Tr>
                                                )}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </GridItem>
                        </Grid>
                      </AnalyticsErrorBoundary>
                    </TabPanel>

                    {/* TAB 2: User Activity */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Activity Error" message="Unable to load the User Activity tab. Please try refreshing.">
                        {/* Activity Summary KPIs - Row 1 */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={5}>
                            <KpiCard 
                                label="Deals Touched" 
                                value={activitySummary.total_deals_touched || 0} 
                                subtext="in date range"
                                loading={loading}
                                icon={FiActivity}
                                colorScheme="purple"
                                tooltipLabel="Count of unique deals with any activity logged (notes, calls, emails) by selected users in the date range"
                            />
                            <KpiCard 
                                label="Avg Deals/Day" 
                                value={activitySummary.avg_deals_touched_per_day || 0} 
                                subtext="team average"
                                loading={loading}
                                icon={FiActivity}
                                colorScheme="purple"
                                tooltipLabel="Deals Touched divided by the number of unique days with any activity logged"
                            />
                            <KpiCard 
                                label="Total Calls Logged" 
                                value={activitySummary.total_call_dispositions || 0} 
                                subtext="disposition-based"
                                loading={loading}
                                icon={FiPhone}
                                colorScheme="blue"
                                tooltipLabel="Counts calls logged with: Left Message, No Answer, Spoke (all variants), Wrong Number"
                            />
                            <KpiCard 
                                label="Quotes Sent" 
                                value={activitySummary.total_quotes || 0} 
                                subtext="in date range"
                                loading={loading}
                                icon={FiMail}
                                colorScheme="green"
                                tooltipLabel="Count of notes logged with disposition 'Sent Quote' in the date range"
                            />
                        </SimpleGrid>
                        
                        {/* Activity Summary KPIs - Row 2: Appointments & Apps */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={6}>
                            <KpiCard 
                                label="Appointments Set" 
                                value={safeReduce(appointmentsAppsData, (sum, u) => sum + safeNumber(u.appointments_set), 0)} 
                                subtext="meetings scheduled"
                                loading={loading}
                                icon={FiCalendar}
                                colorScheme="teal"
                                tooltipLabel="Count of notes logged with disposition 'Scheduled Meeting' in the date range"
                            />
                            <KpiCard 
                                label="Apps Taken" 
                                value={safeReduce(appointmentsAppsData, (sum, u) => sum + safeNumber(u.apps_taken), 0)} 
                                subtext="loan applications"
                                loading={loading}
                                icon={FiCheckSquare}
                                colorScheme="orange"
                                tooltipLabel="Count of deals that moved to 'App Intake' stage during the date range"
                            />
                            <KpiCard 
                                label="Emails Sent" 
                                value={activitySummary.total_emails || 0} 
                                subtext="in date range"
                                loading={loading}
                                icon={FiMail}
                                colorScheme="cyan"
                                tooltipLabel="Count of notes logged with disposition 'Sent Email' in the date range"
                            />
                            <KpiCard 
                                label="Meetings Scheduled" 
                                value={activitySummary.total_meetings || 0} 
                                subtext="from activity logs"
                                loading={loading}
                                icon={FiCalendar}
                                colorScheme="pink"
                                tooltipLabel="Count of notes logged with disposition 'Scheduled Meeting' in the date range (same as Appointments Set)"
                            />
                        </SimpleGrid>

                        {/* User Activity Detail Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px">
                            <Heading size="md" mb={2}>User Activity Breakdown</Heading>
                            <Text fontSize="sm" color="gray.500" mb={6}>
                                Call counts are based on logged dispositions (Left Message, No Answer, Spoke, Wrong Number)
                            </Text>
                            {loading ? <Skeleton height="300px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Name</Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Unique deals with activity logged">
                                                        Deals Touched
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Average deals worked per active day">
                                                        Avg/Day
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>Left Msg</Th>
                                                <Th isNumeric>No Answer</Th>
                                                <Th isNumeric>Spoke</Th>
                                                <Th isNumeric>Wrong #</Th>
                                                <Th isNumeric>Total Calls</Th>
                                                <Th isNumeric>Emails</Th>
                                                <Th isNumeric>Quotes</Th>
                                                <Th isNumeric>Meetings</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {userActivityData.map((stat, index) => (
                                                <Tr key={index}>
                                                    <Td fontWeight="bold">{stat.user_name || 'Unknown'}</Td>
                                                    <Td isNumeric fontWeight="semibold">{stat.deals_touched}</Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={parseFloat(stat.avg_deals_per_day) >= 5 ? 'green' : parseFloat(stat.avg_deals_per_day) >= 2 ? 'yellow' : 'red'}>
                                                            {stat.avg_deals_per_day}
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>{stat.calls_left_message}</Td>
                                                    <Td isNumeric>{stat.calls_no_answer}</Td>
                                                    <Td isNumeric>{stat.calls_spoke}</Td>
                                                    <Td isNumeric>{stat.calls_wrong_number}</Td>
                                                    <Td isNumeric fontWeight="bold" color="blue.600">{stat.total_call_dispositions}</Td>
                                                    <Td isNumeric>{stat.emails_sent}</Td>
                                                    <Td isNumeric>{stat.quotes_sent}</Td>
                                                    <Td isNumeric>{stat.meetings_scheduled}</Td>
                                                </Tr>
                                            ))}
                                            {userActivityData.length === 0 && (
                                                <Tr><Td colSpan={11} textAlign="center" py={8}>No activity data for this period</Td></Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                      </AnalyticsErrorBoundary>
                    </TabPanel>

                    {/* TAB 3: Task Health */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Task Health Error" message="Unable to load the Task Health tab. Please try refreshing.">
                        {/* Task Health Summary KPIs */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={6} data-tour="analytics-task-kpis">
                            <KpiCard 
                                label="Avg Tasks/Deal" 
                                value={taskHealthSummary.avg_tasks_per_deal || '0'} 
                                subtext="across active deals"
                                loading={loading}
                                icon={FiCheckSquare}
                                colorScheme="teal"
                                tooltipLabel="Average number of active tasks per deal (excludes Won/Lost deals)"
                            />
                            <KpiCard 
                                label="Deals Without Tasks" 
                                value={taskHealthSummary.deals_without_tasks || 0} 
                                subtext={`${taskHealthSummary.pct_no_tasks || 0}% of active deals`}
                                loading={loading}
                                icon={FiAlertTriangle}
                                colorScheme="orange"
                                clickable={true}
                                onClick={handleDealsWithoutTasksClick}
                                tooltipLabel="Active deals (not Won/Lost) with zero pending tasks. Click to view the list."
                            />
                            <KpiCard 
                                label="Deals With Overdue" 
                                value={taskHealthSummary.deals_with_overdue || 0} 
                                subtext={`${taskHealthSummary.pct_with_overdue || 0}% at risk`}
                                loading={loading}
                                icon={FiAlertTriangle}
                                colorScheme="red"
                                clickable={true}
                                onClick={handleDealsWithOverdueClick}
                                tooltipLabel="Deals with at least one overdue task assigned to selected user(s). Click to view the list."
                            />
                            <KpiCard 
                                label="Total Overdue Tasks" 
                                value={taskHealthSummary.total_overdue_tasks || 0} 
                                subtext="need attention"
                                loading={loading}
                                icon={FiCalendar}
                                colorScheme="red"
                                clickable={true}
                                onClick={handleOverdueTasksClick}
                                tooltipLabel="All overdue tasks assigned to selected user(s), including tasks not linked to deals. Click to view details."
                            />
                        </SimpleGrid>

                        {/* Task Performance by User Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" data-tour="analytics-task-table">
                            <Heading size="md" mb={2}>Task Performance by User</Heading>
                            <Text fontSize="sm" color="gray.500" mb={6}>
                                All tasks assigned to each user (includes tasks not linked to deals)
                            </Text>
                            {loading ? <Skeleton height="300px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Name</Th>
                                                <Th isNumeric>Active Deals</Th>
                                                <Th isNumeric>Total Tasks</Th>
                                                <Th isNumeric>Avg Tasks/Deal</Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Deals with zero tasks assigned">
                                                        No Tasks
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Deals that have at least one overdue task">
                                                        With Overdue
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>Overdue Tasks</Th>
                                                <Th isNumeric>% No Tasks</Th>
                                                <Th isNumeric>% Overdue</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {taskAnalyticsData.map((stat, index) => (
                                                <Tr key={index}>
                                                    <Td fontWeight="bold">{stat.user_name || 'Unknown'}</Td>
                                                    <Td isNumeric>{stat.total_active_deals}</Td>
                                                    <Td isNumeric>{stat.total_tasks}</Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={parseFloat(stat.avg_tasks_per_deal) >= 2 ? 'green' : parseFloat(stat.avg_tasks_per_deal) >= 1 ? 'yellow' : 'red'}>
                                                            {stat.avg_tasks_per_deal}
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Text color={parseInt(stat.deals_without_tasks) > 0 ? 'orange.500' : 'inherit'} fontWeight={parseInt(stat.deals_without_tasks) > 0 ? 'bold' : 'normal'}>
                                                            {stat.deals_without_tasks}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Text color={parseInt(stat.deals_with_overdue_tasks) > 0 ? 'red.500' : 'inherit'} fontWeight={parseInt(stat.deals_with_overdue_tasks) > 0 ? 'bold' : 'normal'}>
                                                            {stat.deals_with_overdue_tasks}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={parseInt(stat.total_overdue_tasks) === 0 ? 'green' : 'red'}>
                                                            {stat.total_overdue_tasks}
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={parseFloat(stat.pct_deals_no_tasks) === 0 ? 'green' : parseFloat(stat.pct_deals_no_tasks) < 25 ? 'yellow' : 'red'}>
                                                            {stat.pct_deals_no_tasks}%
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={parseFloat(stat.pct_deals_overdue) === 0 ? 'green' : parseFloat(stat.pct_deals_overdue) < 15 ? 'yellow' : 'red'}>
                                                            {stat.pct_deals_overdue}%
                                                        </Badge>
                                                    </Td>
                                                </Tr>
                                            ))}
                                            {taskAnalyticsData.length === 0 && (
                                                <Tr><Td colSpan={9} textAlign="center" py={8}>No task data available</Td></Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                      </AnalyticsErrorBoundary>
                    </TabPanel>
                    
                    {/* TAB 4: Lead Health (NEW) */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Lead Health Error" message="Unable to load the Lead Health tab. Please try refreshing.">
                        {/* Lead Health KPIs */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={6} data-tour="analytics-lead-kpis">
                            <KpiCard 
                                label="Stagnant Leads" 
                                value={stagnantLeads.length} 
                                subtext="no activity in threshold"
                                loading={loading}
                                icon={FiAlertTriangle}
                                colorScheme="orange"
                                clickable={true}
                                onClick={handleStagnantClick}
                                tooltipLabel="Leads with no activity: 7+ days for lead stages, 14+ days for pipeline stages. Click to view filtered by selected user(s)."
                            />
                            <KpiCard 
                                label="No Follow-up Scheduled" 
                                value={leadsWithoutFollowup.length} 
                                subtext="active leads at risk"
                                loading={loading}
                                icon={FiCalendar}
                                colorScheme="red"
                                clickable={true}
                                onClick={handleNoFollowupClick}
                                tooltipLabel="Active leads with zero pending/in-progress tasks scheduled. Click to view filtered by selected user(s)."
                            />
                            <KpiCard 
                                label="Avg Speed to Lead" 
                                value={
                                    speedToLeadData.length > 0 
                                        ? `${Math.round(safeReduce(speedToLeadData, (sum, u) => sum + safeNumber(u.avg_response_minutes), 0) / speedToLeadData.length)} min`
                                        : 'N/A'
                                } 
                                subtext="business hours adjusted"
                                loading={loading}
                                icon={FiClock}
                                colorScheme="blue"
                                tooltipLabel="Average time from lead creation to first logged activity. Calculated during business hours only (M-F, 9am-5pm). Leads arriving outside hours start clock at next business day 9 AM."
                            />
                            <KpiCard 
                                label="Total Active Deals" 
                                value={taskHealthSummary.total_active_deals || 0} 
                                subtext="excluding won/lost"
                                loading={loading}
                                icon={FiActivity}
                                colorScheme="purple"
                                tooltipLabel="Total count of deals not in 'Won' or 'Lost' stage for selected user(s)"
                            />
                        </SimpleGrid>

                        {/* Speed to Lead by User Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" mb={6}>
                            <Heading size="md" mb={2}>Speed to Lead by User</Heading>
                            <Text fontSize="sm" color="gray.500" mb={6}>
                                Response times adjusted for business hours (M-F, 9am-5pm) - leads arriving outside hours start clock at next business day 9 AM
                            </Text>
                            {loading ? <Skeleton height="200px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Name</Th>
                                                <Th isNumeric>Total Leads</Th>
                                                <Th isNumeric>Responded</Th>
                                                <Th isNumeric>No Response</Th>
                                                <Th isNumeric>Avg Response (min)</Th>
                                                <Th isNumeric>Avg Response (hrs)</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {speedToLeadData.map((stat, index) => (
                                                <Tr key={index}>
                                                    <Td fontWeight="bold">{stat.user_name || 'Unknown'}</Td>
                                                    <Td isNumeric>{stat.total_leads}</Td>
                                                    <Td isNumeric>{stat.leads_with_response}</Td>
                                                    <Td isNumeric>
                                                        <Text color={parseInt(stat.leads_no_response) > 0 ? 'red.500' : 'inherit'}>
                                                            {stat.leads_no_response}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={
                                                            stat.avg_response_minutes === null ? 'gray' :
                                                            parseInt(stat.avg_response_minutes) <= 15 ? 'green' :
                                                            parseInt(stat.avg_response_minutes) <= 60 ? 'yellow' : 'red'
                                                        }>
                                                            {stat.avg_response_minutes || 'N/A'}
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>{stat.avg_response_hours || 'N/A'}</Td>
                                                </Tr>
                                            ))}
                                            {speedToLeadData.length === 0 && (
                                                <Tr><Td colSpan={6} textAlign="center" py={8}>No speed to lead data available</Td></Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        {/* Stagnant Leads Preview Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" data-tour="analytics-stagnant">
                            <Flex justify="space-between" align="center" mb={4}>
                                <Box>
                                    <Heading size="md">Stagnant Leads</Heading>
                                    <Text fontSize="sm" color="gray.500">
                                        Leads with no activity beyond threshold (7 days for leads, 14 days for pipeline)
                                    </Text>
                                </Box>
                                <Button size="sm" colorScheme="blue" variant="outline" onClick={handleStagnantClick}>
                                    View All ({stagnantLeads.length})
                                </Button>
                            </Flex>
                            {loading ? <Skeleton height="200px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Deal Name</Th>
                                                <Th>Stage</Th>
                                                <Th>Owner</Th>
                                                <Th isNumeric>Days Stagnant</Th>
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {stagnantLeads.slice(0, 10).map((lead, index) => (
                                                <Tr key={index}>
                                                    <Td fontWeight="bold">{lead.dealName || 'Unknown'}</Td>
                                                    <Td>
                                                        <Badge colorScheme="gray">
                                                            {getStageLabel(lead.stage)}
                                                        </Badge>
                                                    </Td>
                                                    <Td>{lead.owner_name || 'Unassigned'}</Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={
                                                            parseInt(lead.days_stagnant) > 30 ? 'red' :
                                                            parseInt(lead.days_stagnant) > 14 ? 'orange' : 'yellow'
                                                        }>
                                                            {lead.days_stagnant} days
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Link as={RouterLink} to={`/crm/deals/${lead.deal_id}`} color="blue.500" fontSize="sm">
                                                            View Deal
                                                        </Link>
                                                    </Td>
                                                </Tr>
                                            ))}
                                            {stagnantLeads.length === 0 && (
                                                <Tr><Td colSpan={5} textAlign="center" py={8}>No stagnant leads - great job!</Td></Tr>
                                            )}
                                            {stagnantLeads.length > 10 && (
                                                <Tr>
                                                    <Td colSpan={5} textAlign="center" py={2}>
                                                        <Button size="xs" variant="ghost" onClick={handleStagnantClick}>
                                                            + {stagnantLeads.length - 10} more...
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                      </AnalyticsErrorBoundary>
                    </TabPanel>
                    
                    {/* TAB 5: Monday Report */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Monday Report Error" message="Unable to load the Monday Report tab. Please try refreshing.">
                        {/* Monday Report Description + Copy Button */}
                        <Flex mb={6} gap={4} align="stretch" direction={{ base: 'column', md: 'row' }}>
                            <Box flex="1" p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                                <Text fontSize="sm" color="blue.800">
                                    <strong>Monday Report</strong> tracks leads created in the selected date range and their current status:
                                    <br />• <strong>All metrics</strong> = Based on leads created in the date range and their current stage
                                    <br />• <strong>Closings</strong> = Exception - shows deals that closed during the date range (regardless of when created)
                                </Text>
                            </Box>
                            {isCRMAdmin && (
                            <HStack spacing={2} alignSelf="center" flexShrink={0} data-tour="analytics-monday-share">
                                <Button
                                    leftIcon={<FiCopy />}
                                    colorScheme="blue"
                                    variant="outline"
                                    size="md"
                                    onClick={copyMondayReportToClipboard}
                                    isDisabled={loading || !mondayReportData.byUser?.length}
                                    whiteSpace="nowrap"
                                >
                                    Copy for Email
                                </Button>
                                <Button
                                    leftIcon={<FiMail />}
                                    colorScheme="green"
                                    variant="outline"
                                    size="md"
                                    onClick={sendMondayReportEmailHandler}
                                    isLoading={sendingEmail}
                                    loadingText="Sending..."
                                    isDisabled={loading || !mondayReportData.byUser?.length || sendingEmail}
                                    whiteSpace="nowrap"
                                >
                                    Send Report Email
                                </Button>
                            </HStack>
                            )}
                        </Flex>
                        
                        {/* Summary KPIs */}
                        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={5} mb={6} data-tour="analytics-monday-kpis">
                            <KpiCard 
                                label="Total New Leads" 
                                value={mondayReportData.totals?.total_leads || 0}
                                subtext="created in date range"
                                loading={loading}
                                icon={FiUsers}
                                colorScheme="blue"
                                tooltipLabel="Count of new leads created within the selected date range. Click to see all leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('total_leads')}
                            />
                            <KpiCard 
                                label="Avg STL (Biz Hrs)" 
                                value={`${safeNumber(mondayReportData.totals?.avg_stl_biz || 0).toFixed(0)} min`}
                                subtext="9-5 M-F EST"
                                loading={loading}
                                icon={FiClock}
                                colorScheme="teal"
                                tooltipLabel="Average speed to lead calculated during business hours only (Mon-Fri 9am-5pm EST). Excludes weekends and after-hours time."
                            />
                            <KpiCard 
                                label="Contact Rate" 
                                value={`${safeNumber(mondayReportData.totals?.contacted_pct || 0).toFixed(1)}%`}
                                subtext="cohort contacted"
                                loading={loading}
                                icon={FiPhone}
                                colorScheme="green"
                                tooltipLabel="Percentage of cohort leads that have been contacted. Click to see contacted leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('contacted')}
                            />
                            <KpiCard 
                                label="Closings (Period)" 
                                value={mondayReportData.totals?.closings_period || 0}
                                subtext="funded in date range"
                                loading={loading}
                                icon={FiCheckSquare}
                                colorScheme="purple"
                                tooltipLabel="Deals moved to 'Won' during the date range (regardless of creation date). Click to see deals."
                                clickable
                                onClick={() => openMondayCardDrilldown('closings_period')}
                            />
                            <KpiCard 
                                label="Lost (Period)" 
                                value={mondayReportData.totals?.leads_lost_cohort || 0}
                                subtext={`${safeNumber(mondayReportData.totals?.lost_cohort_pct || 0).toFixed(1)}% of cohort`}
                                loading={loading}
                                icon={FiAlertTriangle}
                                colorScheme="red"
                                tooltipLabel="Leads created in the date range that are currently in a Lost stage. Click to see leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('lost_cohort')}
                            />
                        </SimpleGrid>
                        
                        {/* Secondary KPIs Row */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={6}>
                            <KpiCard 
                                label="Conversations" 
                                value={mondayReportData.totals?.leads_with_conv || 0}
                                subtext={`${safeNumber(mondayReportData.totals?.conv_pct || 0).toFixed(1)}% of cohort`}
                                loading={loading}
                                icon={FiPhone}
                                colorScheme="cyan"
                                tooltipLabel="Cohort leads with any Spoke disposition or Scheduled Meeting. Click to see leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('conversations')}
                            />
                            <KpiCard 
                                label="Active Leads" 
                                value={mondayReportData.totals?.active_leads || 0}
                                subtext="from cohort, still active"
                                loading={loading}
                                icon={FiActivity}
                                colorScheme="orange"
                                tooltipLabel="Leads created in the date range that are currently NOT in Won or Lost stages. Click to see leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('active_leads')}
                            />
                            <KpiCard 
                                label="In Application" 
                                value={mondayReportData.totals?.in_app || 0}
                                subtext="from cohort"
                                loading={loading}
                                icon={FiCheckSquare}
                                colorScheme="yellow"
                                tooltipLabel="Leads created in the date range currently in 'App Intake' stage. Click to see leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('in_app')}
                            />
                            <KpiCard 
                                label="In Processing" 
                                value={mondayReportData.totals?.in_processing || 0}
                                subtext="from cohort"
                                loading={loading}
                                icon={FiActivity}
                                colorScheme="pink"
                                tooltipLabel="Leads created in the date range currently in 'Submitted' or 'Processing' stages. Click to see leads."
                                clickable
                                onClick={() => openMondayCardDrilldown('in_processing')}
                            />
                        </SimpleGrid>
                        
                        {/* Main User Performance Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px">
                            <Heading size="md" mb={2}>
                                User Performance Breakdown
                                <Text as="span" fontSize="sm" fontWeight="normal" color="gray.500" ml={2}>
                                    {(() => {
                                        const { start, end } = getDateRangeFromCode(dateRangeCode, customStartDate, customEndDate);
                                        const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                        return '\u2014 ' + fmt(start) + ' to ' + fmt(end);
                                    })()}
                                </Text>
                            </Heading>
                            <Text fontSize="sm" color="gray.500" mb={6}>
                                All metrics by user. STL = Speed to Lead (minutes). Biz = Business Hours only.
                            </Text>
                            {loading ? <Skeleton height="400px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>User</Th>
                                                <Th isNumeric>
                                                    <Tooltip label="New leads created in date range">
                                                        <span>Total New Leads</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Raw speed to lead (24/7 minutes)">
                                                        <span>STL (min)</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Business hours STL (M-F 9-5 EST)">
                                                        <span>STL Biz</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort leads currently not in Won/Lost stages">
                                                        <span>Active</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort: Leads with conversations (spoke/meeting)">
                                                        <span>Conv</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>Conv %</Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort: Leads that have been contacted">
                                                        <span>Contacted</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>Contact %</Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Period: Deals lost during date range">
                                                        <span>Lost (P)</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort: % of cohort leads now lost">
                                                        <span>Lost %</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort leads currently in App Intake stage">
                                                        <span>In App</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort leads currently pre-approved (stage or tag)">
                                                        <span>Pre-App</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Cohort leads currently in Submitted/Processing stages">
                                                        <span>Processing</span>
                                                    </Tooltip>
                                                </Th>
                                                <Th isNumeric>
                                                    <Tooltip label="Period: Deals won during date range">
                                                        <span>Closings</span>
                                                    </Tooltip>
                                                </Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {mondayReportData.byUser?.map((user, idx) => (
                                                <Tr key={idx}>
                                                    <Td fontWeight="bold">{user.user_name || 'Unknown'}</Td>
                                                    <Td isNumeric fontWeight="semibold">{safeNumber(user.total_leads)}</Td>
                                                    <Td isNumeric>{safeNumber(user.avg_stl) > 0 ? safeNumber(user.avg_stl).toFixed(0) : 'N/A'}</Td>
                                                    <Td isNumeric>
                                                        {safeNumber(user.avg_stl_biz) > 0 ? (
                                                            <Badge colorScheme={
                                                                safeNumber(user.avg_stl_biz) <= 30 ? 'green' : 
                                                                safeNumber(user.avg_stl_biz) <= 60 ? 'yellow' : 'red'
                                                            }>
                                                                {safeNumber(user.avg_stl_biz).toFixed(0)}
                                                            </Badge>
                                                        ) : 'N/A'}
                                                    </Td>
                                                    <Td isNumeric>{safeNumber(user.active_leads)}</Td>
                                                    <Td isNumeric>{safeNumber(user.leads_with_conv)}</Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={
                                                            safeNumber(user.conv_pct) >= 20 ? 'green' : 
                                                            safeNumber(user.conv_pct) >= 10 ? 'yellow' : 'red'
                                                        }>
                                                            {safeNumber(user.conv_pct).toFixed(1)}%
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>{safeNumber(user.leads_contacted)}</Td>
                                                    <Td isNumeric>
                                                        <Badge colorScheme={
                                                            safeNumber(user.contacted_pct) >= 80 ? 'green' : 
                                                            safeNumber(user.contacted_pct) >= 50 ? 'yellow' : 'red'
                                                        }>
                                                            {safeNumber(user.contacted_pct).toFixed(1)}%
                                                        </Badge>
                                                    </Td>
                                                    <Td isNumeric>{safeNumber(user.lost_period)}</Td>
                                                    <Td isNumeric>
                                                        <Text color={safeNumber(user.lost_cohort_pct) > 30 ? 'red.500' : 'inherit'}>
                                                            {safeNumber(user.lost_cohort_pct).toFixed(1)}%
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric>{safeNumber(user.in_app)}</Td>
                                                    <Td isNumeric>{safeNumber(user.pre_approved)}</Td>
                                                    <Td isNumeric>{safeNumber(user.in_processing)}</Td>
                                                    <Td isNumeric fontWeight="bold" color="green.600">{safeNumber(user.closings_period)}</Td>
                                                </Tr>
                                            ))}
                                            {(!mondayReportData.byUser || mondayReportData.byUser.length === 0) && (
                                                <Tr>
                                                    <Td colSpan={15} textAlign="center" py={8}>
                                                        No data available for the selected filters and date range
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Tbody>
                                        {mondayReportData.byUser?.length > 0 && (() => {
                                            const t = mondayReportData.totals || {};
                                            return (
                                                <Tfoot>
                                                    <Tr bg="gray.100" borderTopWidth="2px" borderTopColor="gray.300">
                                                        <Td fontWeight="bold">Totals</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.total_leads)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.avg_stl) > 0 ? safeNumber(t.avg_stl).toFixed(0) : 'N/A'}</Td>
                                                        <Td isNumeric fontWeight="bold">
                                                            {safeNumber(t.avg_stl_biz) > 0 ? safeNumber(t.avg_stl_biz).toFixed(0) : 'N/A'}
                                                        </Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.active_leads)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.leads_with_conv)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.conv_pct).toFixed(1)}%</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.leads_contacted)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.contacted_pct).toFixed(1)}%</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.lost_period)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.lost_cohort_pct).toFixed(1)}%</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.in_app)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.pre_approved)}</Td>
                                                        <Td isNumeric fontWeight="bold">{safeNumber(t.in_processing)}</Td>
                                                        <Td isNumeric fontWeight="bold" color="green.600">{safeNumber(t.closings_period)}</Td>
                                                    </Tr>
                                                </Tfoot>
                                            );
                                        })()}
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        {/* Lead Detail Table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" mt={6}>
                            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={3}>
                                <HStack spacing={3}>
                                    <Heading size="md">Lead Detail</Heading>
                                    <Badge colorScheme="blue" fontSize="sm" px={2} py={0.5} borderRadius="full">
                                        {mondayDetailDeals.length} lead{mondayDetailDeals.length !== 1 ? 's' : ''}
                                    </Badge>
                                </HStack>
                                <InputGroup maxW="280px" size="sm">
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FiSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by name, source, owner..."
                                        value={mondayDetailSearch}
                                        onChange={(e) => setMondayDetailSearch(e.target.value)}
                                        bg="white"
                                        borderRadius="md"
                                    />
                                </InputGroup>
                            </Flex>
                            <Text fontSize="sm" color="gray.500" mb={4}>
                                Every lead/deal included in the Monday Report metrics above. Click a column header to sort.
                            </Text>
                            {loading ? <Skeleton height="300px" /> : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                {LEAD_DETAIL_COLUMNS.map(col => (
                                                    <Th
                                                        key={col.key}
                                                        cursor="pointer"
                                                        onClick={() => handleMondayDetailSort(col.key)}
                                                        _hover={{ color: 'blue.600' }}
                                                        whiteSpace="nowrap"
                                                    >
                                                        {col.label}
                                                        {mondayDetailSort.key === col.key
                                                            ? (mondayDetailSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                            : ''}
                                                    </Th>
                                                ))}
                                                {visibleDispositionCols.map(col => (
                                                    <Th
                                                        key={col.key}
                                                        cursor="pointer"
                                                        onClick={() => handleMondayDetailSort(col.key)}
                                                        _hover={{ color: 'blue.600' }}
                                                        whiteSpace="nowrap"
                                                        isNumeric
                                                    >
                                                        {col.label}
                                                        {mondayDetailSort.key === col.key
                                                            ? (mondayDetailSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                            : ''}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {mondayDetailDeals.map((deal, idx) => renderLeadDetailRow(deal, idx, visibleDispositionCols))}
                                            {mondayDetailDeals.length === 0 && (
                                                <Tr>
                                                    <Td colSpan={LEAD_DETAIL_COLUMNS.length + visibleDispositionCols.length} textAlign="center" py={8}>
                                                        {mondayDetailSearch.trim()
                                                            ? 'No leads match your search'
                                                            : 'No lead data available for the selected filters and date range'}
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Tbody>
                                        {mondayDetailDeals.length > 0 && (
                                            <Tfoot>
                                                <Tr bg="gray.100" borderTopWidth="2px" borderTopColor="gray.300">
                                                    <Td fontWeight="bold">Totals ({mondayDetailDeals.length})</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td fontWeight="bold">{leadDetailTotals.contacted} / {mondayDetailDeals.length}</Td>
                                                    <Td fontWeight="bold">{leadDetailTotals.conversation} / {mondayDetailDeals.length}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    <Td>{'\u2014'}</Td>
                                                    {visibleDispositionCols.map(col => (
                                                        <Td key={col.key} isNumeric fontWeight="bold">
                                                            {leadDetailTotals[col.key] || 0}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            </Tfoot>
                                        )}
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                      </AnalyticsErrorBoundary>
                    </TabPanel>

                    {/* TAB 6: Campaigns */}
                    <TabPanel p={6}>
                      <AnalyticsErrorBoundary title="Campaigns Error" message="Unable to load the Campaigns tab. Please try refreshing.">
                        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                            <Text fontSize="xs" color="gray.500">
                                Respects the Users filter and date range. Stage / Source / Tag filters do not apply to campaigns.
                            </Text>
                            <InputGroup maxW="280px" size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FiSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search by name, category, trigger..."
                                    value={campaignSearch}
                                    onChange={(e) => setCampaignSearch(e.target.value)}
                                    bg="white"
                                    borderRadius="md"
                                />
                            </InputGroup>
                        </Flex>

                        {/* KPI Row 1: Enrollment health */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={5} data-tour="analytics-campaigns-kpis">
                            <KpiCard
                                label="Active Campaigns"
                                value={campaignAnalytics.summary.active_campaigns || 0}
                                subtext={`of ${campaignAnalytics.summary.total_campaigns || 0} total`}
                                loading={loading}
                                icon={FiActivity}
                                tooltipLabel="Campaigns that are enabled and have at least one active runner subscription."
                            />
                            <KpiCard
                                label="Enrollments"
                                value={campaignAnalytics.summary.enrolled || 0}
                                subtext="started in range"
                                loading={loading}
                                icon={FiUsers}
                                colorScheme="blue"
                                tooltipLabel="Distinct contacts enrolled per campaign whose first step falls in the selected date range."
                            />
                            <KpiCard
                                label="Active Now"
                                value={campaignAnalytics.summary.active_now || 0}
                                subtext="currently in a step"
                                loading={loading}
                                icon={FiClock}
                                colorScheme="cyan"
                            />
                            <KpiCard
                                label="Completion Rate"
                                value={`${safeNumber(campaignAnalytics.summary.completion_rate)}%`}
                                subtext={`${campaignAnalytics.summary.completed || 0} completed · ${campaignAnalytics.summary.failed || 0} failed`}
                                loading={loading}
                                colorScheme="green"
                                tooltipLabel="Completed enrollments / (completed + failed). Enrollments still active are excluded."
                            />
                        </SimpleGrid>

                        {/* KPI Row 2: Engagement + outcomes */}
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={5} mb={8}>
                            <KpiCard
                                label="Emails Sent"
                                value={campaignAnalytics.summary.emails_sent || 0}
                                subtext={`${campaignAnalytics.summary.sms_sent || 0} SMS sent`}
                                loading={loading}
                                icon={FiMail}
                            />
                            <KpiCard
                                label="Open Rate"
                                value={`${safeNumber(campaignAnalytics.summary.open_rate)}%`}
                                subtext={`${campaignAnalytics.summary.unique_opens || 0} unique opens`}
                                loading={loading}
                                colorScheme="green"
                                tooltipLabel="Emails with at least one tracked open / emails sent by campaigns in range."
                            />
                            <KpiCard
                                label="Click Rate"
                                value={`${safeNumber(campaignAnalytics.summary.click_rate)}%`}
                                subtext={`${campaignAnalytics.summary.unique_clicks || 0} unique clicks`}
                                loading={loading}
                                colorScheme="purple"
                            />
                            <KpiCard
                                label="Funded Volume"
                                value={`$${Math.round(safeNumber(campaignAnalytics.summary.funded_volume)).toLocaleString()}`}
                                subtext={`${campaignAnalytics.summary.funded_deals || 0} funded deals (attributed)`}
                                loading={loading}
                                colorScheme="green"
                                tooltipLabel="Campaign-attributed deals that funded during the selected date range."
                            />
                        </SimpleGrid>

                        {/* Top campaigns chart */}
                        {chartReady && hasValidChartData(topCampaignChartData, ['enrolled']) && topCampaignChartData.length > 0 && (
                            <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" mb={8}>
                                <Heading size="md" mb={4}>Top Campaigns by Enrollments</Heading>
                                <Box h="300px">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={topCampaignChartData} margin={{ top: 5, right: 20, bottom: 10, left: 0 }}>
                                            <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} tick={{ fontSize: 11 }} height={70} />
                                            <YAxis allowDecimals={false} domain={[0, getChartDomainMax(topCampaignChartData, ['enrolled'])]} />
                                            <RechartsTooltip />
                                            <Legend verticalAlign="top" />
                                            <Bar dataKey="enrolled" name="Enrolled" fill="#3182CE" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="completed" name="Completed" fill="#38A169" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        )}

                        {/* Per-campaign breakdown table */}
                        <Box bg="gray.50" p={6} borderRadius="lg" borderWidth="1px" data-tour="analytics-campaigns-table">
                            <Heading size="md" mb={4}>Campaign Breakdown</Heading>
                            {loading ? (
                                <Skeleton height="300px" />
                            ) : visibleCampaignRows.length === 0 ? (
                                <Alert status="info" borderRadius="md">
                                    <AlertIcon />
                                    {campaignSearch.trim()
                                        ? 'No campaigns match your search.'
                                        : 'No campaigns found for the current filters.'}
                                </Alert>
                            ) : (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                {CAMPAIGN_TABLE_COLUMNS.map(col => (
                                                    <Th
                                                        key={col.key}
                                                        cursor="pointer"
                                                        onClick={() => handleCampaignSort(col.key)}
                                                        _hover={{ color: 'blue.600' }}
                                                        whiteSpace="nowrap"
                                                        isNumeric={col.numeric}
                                                    >
                                                        {col.label}
                                                        {campaignSort.key === col.key
                                                            ? (campaignSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                            : ''}
                                                    </Th>
                                                ))}
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {visibleCampaignRows.map(c => (
                                                <Tr key={c.template_id}>
                                                    <Td fontWeight="semibold" maxW="260px">
                                                        <Flex align="center" gap={1} wrap="wrap">
                                                            <Text as="span" noOfLines={1} maxW="180px">{c.name}</Text>
                                                            {c.is_playbook && <Badge fontSize="9px" colorScheme="purple">Playbook</Badge>}
                                                            {c.execution_scope === 'company' && <Badge fontSize="9px" colorScheme="cyan">Company</Badge>}
                                                            {!c.is_active && <Badge fontSize="9px" colorScheme="gray">Inactive</Badge>}
                                                        </Flex>
                                                    </Td>
                                                    <Td>
                                                        <Badge colorScheme={CAMPAIGN_CATEGORY_COLORS[c.category] || 'gray'} variant="subtle" fontSize="xs">
                                                            {CAMPAIGN_CATEGORY_LABELS[c.category] || c.category || 'Custom'}
                                                        </Badge>
                                                    </Td>
                                                    <Td whiteSpace="nowrap" fontSize="xs">{formatTriggerLabel(c.trigger_type)}</Td>
                                                    <Td isNumeric>{c.steps}</Td>
                                                    <Td isNumeric whiteSpace="nowrap">
                                                        <Tooltip label={`${c.active_subscribers} active of ${c.total_subscribers} total runner subscription${c.total_subscribers !== 1 ? 's' : ''}`}>
                                                            <span>{c.active_subscribers}/{c.total_subscribers}</span>
                                                        </Tooltip>
                                                    </Td>
                                                    <Td isNumeric fontWeight="bold">{c.enrolled}</Td>
                                                    <Td isNumeric>
                                                        {c.active_now > 0
                                                            ? <Badge colorScheme="blue" variant="subtle">{c.active_now}</Badge>
                                                            : <Text as="span" color="gray.400">0</Text>}
                                                    </Td>
                                                    <Td isNumeric color="green.600">{c.completed}</Td>
                                                    <Td isNumeric color={c.failed > 0 ? 'red.600' : 'inherit'}>{c.failed}</Td>
                                                    <Td isNumeric>
                                                        {(c.completed + c.failed) > 0 ? (
                                                            <Flex align="center" gap={2} justify="flex-end">
                                                                <Progress
                                                                    value={c.success_rate}
                                                                    size="sm"
                                                                    colorScheme={c.success_rate >= 90 ? 'green' : c.success_rate >= 70 ? 'yellow' : 'red'}
                                                                    borderRadius="full"
                                                                    w="50px"
                                                                />
                                                                <Text fontSize="xs">{c.success_rate}%</Text>
                                                            </Flex>
                                                        ) : <Text as="span" color="gray.400">{'\u2014'}</Text>}
                                                    </Td>
                                                    <Td isNumeric>{c.emails_sent}</Td>
                                                    <Td isNumeric>{c.emails_sent > 0 ? `${c.open_rate}%` : '\u2014'}</Td>
                                                    <Td isNumeric>{c.emails_sent > 0 ? `${c.click_rate}%` : '\u2014'}</Td>
                                                    <Td isNumeric whiteSpace="nowrap">
                                                        {c.sms_sent}
                                                        {c.sms_failed > 0 && (
                                                            <Text as="span" color="red.500" fontSize="xs">{` (${c.sms_failed} failed)`}</Text>
                                                        )}
                                                    </Td>
                                                    <Td isNumeric whiteSpace="nowrap">
                                                        {c.funded_deals > 0 ? (
                                                            <Badge colorScheme="green" variant="subtle">
                                                                {c.funded_deals} · ${Math.round(c.funded_volume).toLocaleString()}
                                                            </Badge>
                                                        ) : <Text as="span" color="gray.400">{'\u2014'}</Text>}
                                                    </Td>
                                                    <Td whiteSpace="nowrap" fontSize="xs">
                                                        {c.last_activity_at
                                                            ? new Date(c.last_activity_at).toLocaleDateString('en-US', {
                                                                timeZone: 'America/New_York',
                                                                month: 'short', day: 'numeric', year: 'numeric',
                                                            })
                                                            : '\u2014'}
                                                    </Td>
                                                    <Td whiteSpace="nowrap">
                                                        <HStack spacing={1}>
                                                            <Button size="xs" colorScheme="blue" variant="outline" onClick={() => openCampaignDetail(c)}>
                                                                Details
                                                            </Button>
                                                            <Button size="xs" variant="ghost" onClick={() => openCampaignLogs(c)}>
                                                                Logs
                                                            </Button>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                      </AnalyticsErrorBoundary>
                    </TabPanel>
                </TabPanels>
            </Tabs>
            
            {/* Notes Activity Timeline Modal */}
            <Modal isOpen={isNotesModalOpen} onClose={onNotesModalClose} size="3xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent maxH="80vh">
                    <ModalHeader>Activity Notes - {notesModalDealName}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {notesModalLoading ? (
                            <Skeleton height="300px" />
                        ) : notesModalActivities.length === 0 ? (
                            <Text color="gray.500" textAlign="center" py={8}>No activity found for this lead.</Text>
                        ) : (
                            <DealTimeline activities={notesModalActivities} maxHeight="60vh" />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Drill-Down Modals */}
            {/* Overdue Tasks Modal */}
            <Modal isOpen={isOverdueModalOpen} onClose={onOverdueModalClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{drillDownTitle}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {drillDownLoading ? (
                            <Skeleton height="300px" />
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Task</Th>
                                            <Th>Deal</Th>
                                            <Th>Stage</Th>
                                            <Th>Owner</Th>
                                            <Th>Due Date</Th>
                                            <Th isNumeric>Days Overdue</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{item.title || 'Untitled Task'}</Td>
                                                <Td>{item.deal_name || 'Unknown'}</Td>
                                                <Td>
                                                    <Badge colorScheme="gray">
                                                        {getStageLabel(item.deal_stage)}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.owner_name || 'Unassigned'}</Td>
                                                <Td>{item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}</Td>
                                                <Td isNumeric>
                                                    <Badge colorScheme="red">{item.days_overdue} days</Badge>
                                                </Td>
                                                <Td>
                                                    <Link as={RouterLink} to={`/crm/deals/${item.deal_id}`} color="blue.500" fontSize="sm">
                                                        View Deal
                                                    </Link>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {drillDownData.length === 0 && (
                                            <Tr><Td colSpan={7} textAlign="center" py={8}>No overdue tasks found</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {drillDownData.length} total records
                        </Text>
                        <Button onClick={onOverdueModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Stagnant Leads Modal */}
            <Modal isOpen={isStagnantModalOpen} onClose={onStagnantModalClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{drillDownTitle}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {drillDownLoading ? (
                            <Skeleton height="300px" />
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Deal Name</Th>
                                            <Th>Contact</Th>
                                            <Th>Stage</Th>
                                            <Th>Owner</Th>
                                            <Th>Created</Th>
                                            <Th>Last Activity</Th>
                                            <Th isNumeric>Days Stagnant</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{item.deal_name || 'Unknown'}</Td>
                                                <Td>
                                                    {item.contact_first_name || item.contact_last_name 
                                                        ? `${item.contact_first_name || ''} ${item.contact_last_name || ''}`.trim()
                                                        : item.contact_email || item.contact_phone || 'N/A'
                                                    }
                                                </Td>
                                                <Td>
                                                    <Badge colorScheme="gray">
                                                        {getStageLabel(item.stage)}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.owner_name || 'Unassigned'}</Td>
                                                <Td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</Td>
                                                <Td>{item.last_activity_time ? new Date(item.last_activity_time).toLocaleDateString() : 'Never'}</Td>
                                                <Td isNumeric>
                                                    <Badge colorScheme={
                                                        parseInt(item.days_stagnant) > 30 ? 'red' :
                                                        parseInt(item.days_stagnant) > 14 ? 'orange' : 'yellow'
                                                    }>
                                                        {item.days_stagnant} days
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Link as={RouterLink} to={`/crm/deals/${item.deal_id}`} color="blue.500" fontSize="sm">
                                                        View Deal
                                                    </Link>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {drillDownData.length === 0 && (
                                            <Tr><Td colSpan={8} textAlign="center" py={8}>No stagnant leads found</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {drillDownData.length} total records
                        </Text>
                        <Button onClick={onStagnantModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* No Follow-up Modal */}
            <Modal isOpen={isNoFollowupModalOpen} onClose={onNoFollowupModalClose} size="5xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{drillDownTitle}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {drillDownLoading ? (
                            <Skeleton height="300px" />
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Deal Name</Th>
                                            <Th>Stage</Th>
                                            <Th>Owner</Th>
                                            <Th>Created</Th>
                                            <Th>Last Updated</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{item.deal_name || 'Unknown'}</Td>
                                                <Td>
                                                    <Badge colorScheme="gray">
                                                        {getStageLabel(item.stage)}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.owner_name || 'Unassigned'}</Td>
                                                <Td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</Td>
                                                <Td>{item.last_updated ? new Date(item.last_updated).toLocaleDateString() : 'N/A'}</Td>
                                                <Td>
                                                    <Link as={RouterLink} to={`/crm/deals/${item.deal_id}`} color="blue.500" fontSize="sm">
                                                        View Deal
                                                    </Link>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {drillDownData.length === 0 && (
                                            <Tr><Td colSpan={6} textAlign="center" py={8}>All active leads have follow-ups scheduled!</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {drillDownData.length} total records
                        </Text>
                        <Button onClick={onNoFollowupModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Deals Without Tasks Modal */}
            <Modal isOpen={isDealsWithoutTasksModalOpen} onClose={onDealsWithoutTasksModalClose} size="5xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{drillDownTitle}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {drillDownLoading ? (
                            <Skeleton height="300px" />
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Deal Name</Th>
                                            <Th>Stage</Th>
                                            <Th>Owner</Th>
                                            <Th>Created</Th>
                                            <Th>Last Updated</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{item.deal_name || 'Unknown'}</Td>
                                                <Td>
                                                    <Badge colorScheme="gray">
                                                        {getStageLabel(item.stage)}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.owner_name || 'Unassigned'}</Td>
                                                <Td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</Td>
                                                <Td>{item.last_updated ? new Date(item.last_updated).toLocaleDateString() : 'N/A'}</Td>
                                                <Td>
                                                    <Link as={RouterLink} to={`/crm/deals/${item.deal_id}`} color="blue.500" fontSize="sm">
                                                        View Deal
                                                    </Link>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {drillDownData.length === 0 && (
                                            <Tr><Td colSpan={6} textAlign="center" py={8}>All deals have tasks assigned!</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {drillDownData.length} total records
                        </Text>
                        <Button onClick={onDealsWithoutTasksModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Deals With Overdue Tasks Modal */}
            <Modal isOpen={isDealsWithOverdueModalOpen} onClose={onDealsWithOverdueModalClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{drillDownTitle}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {drillDownLoading ? (
                            <Skeleton height="300px" />
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Deal Name</Th>
                                            <Th>Stage</Th>
                                            <Th>Owner</Th>
                                            <Th isNumeric>Overdue Tasks</Th>
                                            <Th>Earliest Overdue</Th>
                                            <Th isNumeric>Days Overdue</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {drillDownData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td fontWeight="bold">{item.deal_name || 'Unknown'}</Td>
                                                <Td>
                                                    <Badge colorScheme="gray">
                                                        {getStageLabel(item.stage)}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.owner_name || 'Unassigned'}</Td>
                                                <Td isNumeric>
                                                    <Badge colorScheme="red">{item.overdue_task_count}</Badge>
                                                </Td>
                                                <Td>{item.earliest_overdue_date ? new Date(item.earliest_overdue_date).toLocaleDateString() : 'N/A'}</Td>
                                                <Td isNumeric>
                                                    <Badge colorScheme="red">{item.days_overdue} days</Badge>
                                                </Td>
                                                <Td>
                                                    <Link as={RouterLink} to={`/crm/deals/${item.deal_id}`} color="blue.500" fontSize="sm">
                                                        View Deal
                                                    </Link>
                                                </Td>
                                            </Tr>
                                        ))}
                                        {drillDownData.length === 0 && (
                                            <Tr><Td colSpan={7} textAlign="center" py={8}>No deals with overdue tasks!</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {drillDownData.length} total deals
                        </Text>
                        <Button onClick={onDealsWithOverdueModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Monday Report Card Drilldown Modal */}
            <Modal isOpen={isMondayCardModalOpen} onClose={onMondayCardModalClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent maxW="90vw">
                    <ModalHeader>
                        {MONDAY_CARD_FILTERS[mondayCardFilterKey]?.label || 'Drilldown'}
                        <Badge ml={3} colorScheme="blue" fontSize="md" px={2} py={0.5} borderRadius="full">
                            {mondayCardDrilldownDeals.length} lead{mondayCardDrilldownDeals.length !== 1 ? 's' : ''}
                        </Badge>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                            <Text fontSize="sm" color="gray.500">
                                Showing leads that make up the "{MONDAY_CARD_FILTERS[mondayCardFilterKey]?.label}" metric.
                            </Text>
                            <InputGroup maxW="280px" size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FiSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search by name, source, owner..."
                                    value={mondayCardSearch}
                                    onChange={(e) => setMondayCardSearch(e.target.value)}
                                    bg="white"
                                    borderRadius="md"
                                />
                            </InputGroup>
                        </Flex>
                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        {LEAD_DETAIL_COLUMNS.map(col => (
                                            <Th
                                                key={col.key}
                                                cursor="pointer"
                                                onClick={() => handleMondayCardSort(col.key)}
                                                _hover={{ color: 'blue.600' }}
                                                whiteSpace="nowrap"
                                            >
                                                {col.label}
                                                {mondayCardSort.key === col.key
                                                    ? (mondayCardSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                    : ''}
                                            </Th>
                                        ))}
                                        {visibleDispositionCols.map(col => (
                                            <Th
                                                key={col.key}
                                                cursor="pointer"
                                                onClick={() => handleMondayCardSort(col.key)}
                                                _hover={{ color: 'blue.600' }}
                                                whiteSpace="nowrap"
                                                isNumeric
                                            >
                                                {col.label}
                                                {mondayCardSort.key === col.key
                                                    ? (mondayCardSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                    : ''}
                                            </Th>
                                        ))}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {mondayCardDrilldownDeals.map((deal, idx) => renderLeadDetailRow(deal, idx, visibleDispositionCols))}
                                    {mondayCardDrilldownDeals.length === 0 && (
                                        <Tr>
                                            <Td colSpan={LEAD_DETAIL_COLUMNS.length + visibleDispositionCols.length} textAlign="center" py={8}>
                                                {mondayCardSearch.trim()
                                                    ? 'No leads match your search'
                                                    : 'No leads found for this metric'}
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                                {mondayCardDrilldownDeals.length > 0 && (
                                    <Tfoot>
                                        <Tr bg="gray.100" borderTopWidth="2px" borderTopColor="gray.300">
                                            <Td fontWeight="bold">Totals ({mondayCardDrilldownDeals.length})</Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td fontWeight="bold">
                                                {mondayCardDrilldownDeals.filter(d => d.contacted === 'Yes').length} / {mondayCardDrilldownDeals.length}
                                            </Td>
                                            <Td fontWeight="bold">
                                                {mondayCardDrilldownDeals.filter(d => d.conversation === 'Yes').length} / {mondayCardDrilldownDeals.length}
                                            </Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td>{'\u2014'}</Td>
                                            <Td>{'\u2014'}</Td>
                                            {visibleDispositionCols.map(col => (
                                                <Td key={col.key} isNumeric fontWeight="bold">
                                                    {mondayCardDrilldownDeals.reduce((sum, d) => sum + ((d.disposition_counts || {})[col.key] || 0), 0)}
                                                </Td>
                                            ))}
                                        </Tr>
                                    </Tfoot>
                                )}
                            </Table>
                        </TableContainer>
                    </ModalBody>
                    <ModalFooter>
                        <Text fontSize="sm" color="gray.500" mr="auto">
                            {mondayCardDrilldownDeals.length} total records
                        </Text>
                        <Button onClick={onMondayCardModalClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Campaign Detail Modal */}
            <Modal isOpen={isCampaignDetailOpen} onClose={onCampaignDetailClose} size="6xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Flex align="center" gap={2} wrap="wrap" pr={8}>
                            <Text>{campaignDetailTarget?.name || 'Campaign'}</Text>
                            {campaignDetail?.template && (
                                <>
                                    <Badge colorScheme={CAMPAIGN_CATEGORY_COLORS[campaignDetail.template.category] || 'gray'} fontSize="xs">
                                        {CAMPAIGN_CATEGORY_LABELS[campaignDetail.template.category] || campaignDetail.template.category}
                                    </Badge>
                                    <Badge colorScheme={campaignDetail.template.is_active ? 'green' : 'gray'} fontSize="xs">
                                        {campaignDetail.template.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                    {campaignDetail.template.is_playbook && <Badge colorScheme="purple" fontSize="xs">Playbook</Badge>}
                                    {campaignDetail.template.execution_scope === 'company' && <Badge colorScheme="cyan" fontSize="xs">Company Automation</Badge>}
                                </>
                            )}
                        </Flex>
                        {campaignDetail?.template?.trigger_type && (
                            <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
                                Trigger: {formatTriggerLabel(campaignDetail.template.trigger_type)}
                            </Text>
                        )}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {campaignDetailLoading ? (
                            <Skeleton height="400px" />
                        ) : !campaignDetail ? (
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                Unable to load campaign details. Please try again.
                            </Alert>
                        ) : (
                            <>
                                {/* Enrollment summary */}
                                <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={5}>
                                    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                                        <Stat>
                                            <StatLabel fontSize="xs" color="gray.500">Enrolled</StatLabel>
                                            <StatNumber fontSize="xl">{campaignDetail.enrollmentSummary?.enrolled || 0}</StatNumber>
                                            <StatHelpText fontSize="xs" mb={0}>in date range</StatHelpText>
                                        </Stat>
                                    </Box>
                                    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                                        <Stat>
                                            <StatLabel fontSize="xs" color="gray.500">Active</StatLabel>
                                            <StatNumber fontSize="xl" color="blue.600">{campaignDetail.enrollmentSummary?.active_now || 0}</StatNumber>
                                            <StatHelpText fontSize="xs" mb={0}>currently in a step</StatHelpText>
                                        </Stat>
                                    </Box>
                                    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                                        <Stat>
                                            <StatLabel fontSize="xs" color="gray.500">Completed</StatLabel>
                                            <StatNumber fontSize="xl" color="green.600">{campaignDetail.enrollmentSummary?.completed || 0}</StatNumber>
                                            <StatHelpText fontSize="xs" mb={0}>{campaignDetail.enrollmentSummary?.cancelled || 0} cancelled</StatHelpText>
                                        </Stat>
                                    </Box>
                                    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                                        <Stat>
                                            <StatLabel fontSize="xs" color="gray.500">Failed</StatLabel>
                                            <StatNumber fontSize="xl" color={(campaignDetail.enrollmentSummary?.failed || 0) > 0 ? 'red.600' : 'inherit'}>
                                                {campaignDetail.enrollmentSummary?.failed || 0}
                                            </StatNumber>
                                            <StatHelpText fontSize="xs" mb={0}>enrollments with a failed step</StatHelpText>
                                        </Stat>
                                    </Box>
                                    <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                                        <Stat>
                                            <StatLabel fontSize="xs" color="gray.500">Success Rate</StatLabel>
                                            <StatNumber fontSize="xl">{safeNumber(campaignDetail.enrollmentSummary?.success_rate)}%</StatNumber>
                                            <StatHelpText fontSize="xs" mb={0}>completed / finished</StatHelpText>
                                        </Stat>
                                    </Box>
                                </SimpleGrid>

                                {/* Engagement */}
                                {((campaignDetail.emailStats?.emails_sent || 0) > 0 || (campaignDetail.smsStats?.sms_sent || 0) > 0 || (campaignDetail.smsStats?.sms_failed || 0) > 0) && (
                                    <Flex gap={6} mb={5} wrap="wrap" p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Emails Sent</Text>
                                            <Text fontSize="lg" fontWeight="bold">{campaignDetail.emailStats?.emails_sent || 0}</Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Open Rate</Text>
                                            <Text fontSize="lg" fontWeight="bold" color="green.600">
                                                {safeNumber(campaignDetail.emailStats?.open_rate)}%
                                                <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">{` (${campaignDetail.emailStats?.unique_opens || 0} opens)`}</Text>
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Click Rate</Text>
                                            <Text fontSize="lg" fontWeight="bold" color="purple.600">
                                                {safeNumber(campaignDetail.emailStats?.click_rate)}%
                                                <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">{` (${campaignDetail.emailStats?.unique_clicks || 0} clicks)`}</Text>
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">SMS Sent</Text>
                                            <Text fontSize="lg" fontWeight="bold">
                                                {campaignDetail.smsStats?.sms_sent || 0}
                                                {(campaignDetail.smsStats?.sms_failed || 0) > 0 && (
                                                    <Text as="span" fontSize="xs" color="red.500" fontWeight="normal">{` (${campaignDetail.smsStats.sms_failed} failed)`}</Text>
                                                )}
                                            </Text>
                                        </Box>
                                    </Flex>
                                )}

                                {/* Enrollments by day */}
                                {(campaignDetail.enrollmentsByDay || []).length > 0 && (
                                    <Box mb={5} p={4} borderWidth="1px" borderRadius="md" bg="white">
                                        <Heading size="sm" mb={3}>Enrollments by Day</Heading>
                                        <Box h="180px">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={campaignDetail.enrollmentsByDay} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                                                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                                                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                                    <RechartsTooltip />
                                                    <Bar dataKey="count" name="Enrollments" fill="#3182CE" radius={[3, 3, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                )}

                                {/* Step breakdown */}
                                <Flex align="baseline" gap={2} mb={3} wrap="wrap">
                                    <Heading size="sm">Step Breakdown</Heading>
                                    <Text fontSize="xs" color="gray.500">All-time step totals (not limited to the date range)</Text>
                                </Flex>
                                {(campaignDetail.nodeStats || []).length === 0 ? (
                                    <Alert status="info" borderRadius="md" mb={5}>
                                        <AlertIcon />
                                        This campaign has no executable steps.
                                    </Alert>
                                ) : (
                                    <TableContainer mb={5}>
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th>#</Th>
                                                    <Th>Step</Th>
                                                    <Th isNumeric>Executions</Th>
                                                    <Th isNumeric>Completed</Th>
                                                    <Th isNumeric>Failed</Th>
                                                    <Th isNumeric>Pending</Th>
                                                    <Th isNumeric>Fail %</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {(campaignDetail.nodeStats || []).map((node, idx) => (
                                                    <Tr key={node.node_id || `removed-${node.sub_type}-${idx}`} opacity={node.removed ? 0.7 : 1}>
                                                        <Td color="gray.500">{node.removed ? '\u2014' : idx + 1}</Td>
                                                        <Td maxW="360px">
                                                            <Flex align="center" gap={2}>
                                                                <Badge colorScheme={CAMPAIGN_NODE_COLOR_MAP[node.sub_type] || 'gray'} fontSize="xs" flexShrink={0}>
                                                                    {(node.sub_type || 'UNKNOWN').replace(/_/g, ' ')}
                                                                </Badge>
                                                                <Text fontSize="sm" noOfLines={1}>{node.label}</Text>
                                                            </Flex>
                                                        </Td>
                                                        <Td isNumeric>{node.executions}</Td>
                                                        <Td isNumeric color="green.600">{node.completed}</Td>
                                                        <Td isNumeric color={node.failed > 0 ? 'red.600' : 'inherit'}>{node.failed}</Td>
                                                        <Td isNumeric>{node.pending}</Td>
                                                        <Td isNumeric>
                                                            {node.executions > 0 ? (
                                                                <Badge
                                                                    colorScheme={node.fail_rate >= 25 ? 'red' : node.fail_rate > 0 ? 'yellow' : 'green'}
                                                                    variant="subtle"
                                                                    fontSize="xs"
                                                                >
                                                                    {safeNumber(node.fail_rate)}%
                                                                </Badge>
                                                            ) : '\u2014'}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Recent failures */}
                                {(campaignDetail.recentFailures || []).length > 0 && (
                                    <>
                                        <Heading size="sm" mb={3}>Recent Failures</Heading>
                                        <Box maxH="260px" overflowY="auto" mb={2}>
                                            {(campaignDetail.recentFailures || []).map((f, idx) => (
                                                <Alert key={`${f.contact_id}-${idx}`} status="error" borderRadius="md" mb={2} py={2} alignItems="flex-start">
                                                    <AlertIcon boxSize={4} mt={1} />
                                                    <Box>
                                                        <Text fontSize="sm" fontWeight="semibold">
                                                            {f.contact_name}
                                                            <Text as="span" fontWeight="normal" color="gray.600">
                                                                {` — ${(f.sub_type || 'UNKNOWN').replace(/_/g, ' ')}`}
                                                            </Text>
                                                            {f.created_at && (
                                                                <Text as="span" fontWeight="normal" fontSize="xs" color="gray.500">
                                                                    {` · ${new Date(f.created_at).toLocaleString('en-US', {
                                                                        timeZone: 'America/New_York',
                                                                        month: 'short', day: 'numeric',
                                                                        hour: 'numeric', minute: '2-digit',
                                                                    })}`}
                                                                </Text>
                                                            )}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.700">{f.error_log}</Text>
                                                    </Box>
                                                </Alert>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            colorScheme="blue"
                            mr="auto"
                            isDisabled={!campaignDetailTarget}
                            onClick={() => {
                                onCampaignDetailClose();
                                if (campaignDetailTarget) {
                                    setCampaignLogsTarget({ templateId: campaignDetailTarget.templateId, name: campaignDetailTarget.name });
                                    onCampaignLogsOpen();
                                }
                            }}
                        >
                            Open Contact Logs
                        </Button>
                        <Button onClick={onCampaignDetailClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Campaign contact-logs drawer (self-fetching, reused from Campaigns page) */}
            {campaignLogsTarget && (
                <CampaignLogsDrawer
                    isOpen={isCampaignLogsOpen}
                    onClose={onCampaignLogsClose}
                    templateId={campaignLogsTarget.templateId}
                    campaignName={campaignLogsTarget.name}
                />
            )}
        </Box>
    );
};

export default AnalyticsPage;
